/**
 * Canvas Editor with Auto-Save
 *
 * Fabric.js canvas + toolbar + shape/text tools + save indicator.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import fabric from '@/lib/fabric';
import type { DesignElement, FabricCanvasData, ProjectPage } from '@canvas/shared-types';
import {
  HISTORY_APPLY_EVENT,
  useCanvasStore,
  type CanvasTool,
} from '@/stores/canvasStore';
import { useProjectStore } from '@/stores/projectStore';
import { useAutoSave } from '@/hooks/useAutoSave';
import {
  isCompactLayout,
  readLayoutMode,
  useLayoutMode,
} from '@/hooks/useLayoutMode';
import { insertDesignElement, applyTemplateContent } from '@/lib/insertElement';
import { TEMPLATE_CATALOG } from '@/data/templateCatalog';
import SaveIndicator from './SaveIndicator';
import ToolStrip, { TOOL_SHORTCUTS, nextShapeTool, nextTypeTool } from './ToolStrip';
import OptionsBar from './OptionsBar';
import LayersPanel from './LayersPanel';
import PropertiesPanel from './PropertiesPanel';
import HistoryPanel from './HistoryPanel';
import PagesStrip from './PagesStrip';
import VideoTimelinePanel from './video/VideoTimelinePanel';
import AnimationPanel from './video/AnimationPanel';
import { useVideoPreview } from '@/hooks/useVideoPreview';
import { useLiveVideoLayers } from '@/hooks/useLiveVideoLayers';
import { createVideoObject, isVideoObject, registerVideoObject } from '@/lib/video/fabricVideoObject';
import { uploadProjectVideo } from '@/lib/video/videoAssetUpload';
import { exportAllowed, MOTION_EXPORT_DESKTOP_COPY } from '@/lib/video/browserSupport';
import { motionShortcutFor, trimClipBy, trimSceneBy } from '@/lib/video/motionShortcuts';
import { OPEN_ANIMATION_PANEL_EVENT, OPEN_VIDEO_EXPORT_EVENT, VIDEO_CLIP_CHANGED_EVENT } from '@/lib/video/videoEvents';
import VideoExportDialog from './video/VideoExportDialog';
import { useVideoStore } from '@/stores/videoStore';
import { isPreviewActive } from '@/lib/video/previewLock';
import { assertSceneBudget, estimateProjectDocBytes } from '@/lib/video/videoDocLimits';
import { ensureObjectUid } from '@/lib/objectUid';
import MobileBottomBar from './MobileBottomBar';
import EditorZoomControls from './EditorZoomControls';
import DesignPanel, { type DesignPanelTab, type RecentUpload } from './DesignPanel';
import MobileSheet, { type MobileSheetId } from './MobileSheet';
import MobileToolDock, {
  MobileMoreToolsGrid,
  MobileShapePicker,
  MobileTypePicker,
} from './MobileToolDock';
import ExportDialog from './ExportDialog';
import PresentMode from './PresentMode';
import { OPEN_EXPORT_EVENT } from '@/lib/exportCanvas';
import {
  OPEN_PRESENT_EVENT,
  captureAllPresentSlides,
} from '@/lib/presentMode';
import { registerEditorSaveHandler, requestEditorSave } from '@/lib/editorSave';
import { uploadProjectImage } from '@/lib/uploadAsset';
import { loadFabricImage, placeFabricImage } from '@/lib/loadFabricImage';
import { installStageTouchGestures } from '@/lib/touchGestures';
import {
  createBlankPage,
  duplicatePage,
  ensurePagesMatchCanvas,
  getActivePage,
  movePage,
  normalizeProjectPages,
  resolveProjectForEditor,
  reorderPage,
  replacePageData,
} from '@/lib/projectPages';
import { applyPaintBucket } from '@/lib/paintBucket';
import {
  LAYER_CUSTOM_PROPS,
  isObjectLocked,
  setObjectLocked,
} from '@/lib/layerOps';
import ShortcutsHelp, { OPEN_SHORTCUTS_EVENT } from './ShortcutsHelp';
import {
  alignSelection,
  eraseObjectAtPoint,
  groupSelection,
  ungroupSelection,
} from '@/lib/objectOps';
import {
  arrangeSelection,
  copySelection,
  cutSelection,
  duplicateSelection,
  nudgeSelection,
  pasteClipboard,
  selectAllObjects,
} from '@/lib/selectionClipboard';
import { flipSelection, rotateSelection } from '@/lib/transformOps';
import { registerEditorCommands, clearEditorCommands } from '@/lib/editorCommands';
import {
  RESIZE_ARTBOARD_EVENT,
  resizeArtboard,
} from '@/lib/artboardSize';
import { OPEN_PHOTO_STUDIO_EVENT } from '@/lib/photoStudioEvents';
import {
  createDragShape,
  finalizeArrow,
  finalizeDragPolygon,
  isLinearTool,
  updateDragShape,
} from '@/lib/shapeFactory';
import {
  isShapeRuntimeTool,
  isTypeRuntimeTool,
  type ShapeRuntimeTool,
} from '@canvas/shared-types';
import {
  createFrameObject,
  fillFrameWithImage,
  isFrameObject,
} from '@/lib/frames';
import { applyGradientFill } from '@/lib/gradients';
import {
  createPenPreview,
  finalizePenPath,
  type PenPoint,
} from '@/lib/penPath';
import { sampleColorFromObject } from '@/lib/colorSample';
import {
  applyArtboardCrop,
  applyImageCrop,
  beginImageCrop,
  cancelImageCrop,
  createCropOverlay,
  isCropOverlay,
  removeCropOverlay,
  type CropSession,
} from '@/lib/cropTool';
import {
  createMarqueeOverlay,
  isMarqueeOverlay,
  selectObjectsInMarquee,
} from '@/lib/marqueeTool';
import {
  isLassoOverlay,
  applyLasso,
  findImageAtArtboardPoint,
  lassoPointsAttr,
} from '@/lib/lassoTool';
import { bakeTextObjectScale, installTextDragCreate } from '@/lib/textDragCreate';
import {
  applyGuidesLockState,
  clearGuides,
  clientToArtboard,
  constrainGuideMove,
  createGuide,
  forceReleaseCanvas,
  installCssZoomPointerFix,
  isGuideObject,
  setGuidesLocked,
} from '@/lib/guideLines';
import {
  installSnapGuides,
  setGridSnapSize,
  setSmartSnapEnabled,
} from '@/lib/snapGuides';
import { renderPageToDataUrl } from '@/lib/renderPage';
import { useComments } from '@/hooks/useComments';
import CommentsOverlay from './CommentsOverlay';
import Rulers, { DROP_GUIDE_EVENT } from './Rulers';
import './CanvasEditor.css';
import '../styles/editor-adobe.css';

export const TOGGLE_RULERS_EVENT = 'craftara:toggle-rulers';
export const TOGGLE_LEFT_DOCK_EVENT = 'craftara:toggle-left-dock';
export const TOGGLE_RIGHT_DOCK_EVENT = 'craftara:toggle-right-dock';

function clampZoom(value: number): number {
  return Math.min(200, Math.max(25, Math.round(value)));
}

interface CanvasEditorProps {
  projectId: string;
  autoSaveInterval?: number;
  generateThumbnails?: boolean;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  /** Design never shows the timeline. Movie Studio keeps it on. */
  studio?: 'design' | 'movie';
}

type FabricObject = fabric.Object;

interface DrawSession {
  tool: CanvasTool;
  originX: number;
  originY: number;
  shape: FabricObject | null;
  /** Shift held — additive marquee */
  additive?: boolean;
  /** Marquee rect vs ellipse */
  marqueeShape?: 'rect' | 'ellipse';
  /** Image under lasso start — used for freehand cutout */
  preferredImage?: fabric.Image | null;
}

interface PanSession {
  clientX: number;
  clientY: number;
  scrollLeft: number;
  scrollTop: number;
}

export default function CanvasEditor({
  projectId,
  autoSaveInterval = 10000,
  generateThumbnails = false,
  zoom = 100,
  onZoomChange,
  studio = 'design',
}: CanvasEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const drawSessionRef = useRef<DrawSession | null>(null);
  const panSessionRef = useRef<PanSession | null>(null);
  const eraseActiveRef = useRef(false);
  const eraseDirtyRef = useRef(false);
  const spacePanRef = useRef(false);
  const skipHistoryRef = useRef(false);
  const toolRef = useRef<CanvasTool>('select');
  const colorRef = useRef('#000000');
  const opacityRef = useRef(1);
  const brushSizeRef = useRef(3);
  const gradientToRef = useRef('#0d7377');
  const zoomRef = useRef(zoom);
  const onZoomChangeRef = useRef(onZoomChange);
  const onCanvasChangeRef = useRef<() => void>(() => undefined);
  const altHeldRef = useRef(false);
  const penPointsRef = useRef<PenPoint[]>([]);
  const penPreviewRef = useRef<fabric.Path | null>(null);
  const cropSessionRef = useRef<CropSession | null>(null);
  const startImageCropSessionRef = useRef<(image: fabric.Image) => void>(() => undefined);
  const clearCropSessionRef = useRef<(opts?: { apply?: boolean }) => void>(() => undefined);
  const applyingHistoryRef = useRef(false);
  const pagesRef = useRef<ProjectPage[]>([]);
  const activePageIdRef = useRef('');

  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [cropActive, setCropActive] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** State copy so LayersPanel re-renders when Fabric is ready */
  const [fabricCanvas, setFabricCanvas] = useState<fabric.Canvas | null>(null);
  const layoutMode = useLayoutMode();
  const layoutModeRef = useRef(layoutMode);
  layoutModeRef.current = layoutMode;
  const compactLayout = isCompactLayout(layoutMode);
  const isPhone = layoutMode === 'phone';
  const [mobileSheet, setMobileSheet] = useState<MobileSheetId | null>(null);
  const [leftTab, setLeftTab] = useState<DesignPanelTab>('elements');
  const [leftDockOpen, setLeftDockOpen] = useState(
    () => !isCompactLayout(readLayoutMode())
  );
  const [rightTab, setRightTab] = useState<'properties' | 'layers' | 'history' | 'motion'>(
    'layers'
  );
  const [rightDockOpen, setRightDockOpen] = useState(
    () => !isCompactLayout(readLayoutMode())
  );
  const [pages, setPages] = useState<ProjectPage[]>(() => {
    const project = useProjectStore.getState().currentProject;
    return project ? resolveProjectForEditor(project).pages : [];
  });
  const [activePageId, setActivePageId] = useState(() => {
    const project = useProjectStore.getState().currentProject;
    return project ? resolveProjectForEditor(project).activePageId : '';
  });
  const [pageThumbs, setPageThumbs] = useState<Record<string, string>>({});
  const [exportOpen, setExportOpen] = useState(false);
  const [videoExportOpen, setVideoExportOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [presentOpen, setPresentOpen] = useState(false);
  const [presentSlides, setPresentSlides] = useState<string[]>([]);
  const [recentUploads, setRecentUploads] = useState<RecentUpload[]>([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [altHeld, setAltHeld] = useState(false);
  const [rulersVisible, setRulersVisible] = useState(
    () => readLayoutMode() !== 'phone'
  );
  const prevLayoutModeRef = useRef(layoutMode);

  // Compact layouts: keep stage usable — collapse docks; restore on desktop
  useEffect(() => {
    const prev = prevLayoutModeRef.current;
    if (isCompactLayout(layoutMode)) {
      setLeftDockOpen(false);
      setRightDockOpen(false);
      if (layoutMode === 'phone') setRulersVisible(false);
    } else if (isCompactLayout(prev) && layoutMode === 'desktop') {
      setLeftDockOpen(true);
      setRightDockOpen(true);
    }
    prevLayoutModeRef.current = layoutMode;
  }, [layoutMode]);
  const [gridVisible, setGridVisible] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [guidesLocked, setGuidesLockedState] = useState(false);
  const lassoPointsRef = useRef<Array<{ x: number; y: number }>>([]);
  const lassoPolyRef = useRef<SVGPolylineElement | null>(null);
  const [lassoActive, setLassoActive] = useState(false);
  const touchGestureActiveRef = useRef(false);
  const opacityChordRef = useRef<{ digits: string; at: number } | null>(null);

  const {
    canvasData,
    selectedTool,
    selectedColor,
    gradientToColor,
    brushSize,
    opacity,
    isDirty,
    addToHistory,
    setCanvasData,
    markDirty,
    setTool,
  } = useCanvasStore();

  const { currentProject, updateProject } = useProjectStore();
  const { clearHistory } = useCanvasStore();
  const studioRef = useRef(studio);
  studioRef.current = studio;
  const motionMode = studio === 'movie';
  const {
    comments,
    addComment,
    resolveComment,
    deleteComment,
  } = useComments(projectId);

  pagesRef.current = pages;
  activePageIdRef.current = activePageId;

  const clearCropSession = useCallback(
    (opts?: { apply?: boolean }) => {
      const canvas = fabricCanvasRef.current;
      const session = cropSessionRef.current;
      if (!canvas || !session) {
        cropSessionRef.current = null;
        setCropActive(false);
        return;
      }

      if (opts?.apply) {
        if (session.mode === 'image') {
          applyImageCrop(session);
          removeCropOverlay(canvas, session.overlay);
        } else {
          const size = applyArtboardCrop(canvas, session.overlay);
          removeCropOverlay(canvas, session.overlay);
          const project = useProjectStore.getState().currentProject;
          if (project) {
            updateProject({ ...project, width: size.width, height: size.height });
          }
        }
        cropSessionRef.current = null;
        setCropActive(false);
        onCanvasChangeRef.current();
        useCanvasStore.getState().setTool('select');
        return;
      }

      if (session.mode === 'image') {
        cancelImageCrop(session);
      }
      removeCropOverlay(canvas, session.overlay);
      cropSessionRef.current = null;
      setCropActive(false);
      canvas.requestRenderAll();
    },
    [updateProject]
  );

  const startImageCropSession = useCallback((image: fabric.Image) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    if (cropSessionRef.current) {
      const prev = cropSessionRef.current;
      if (prev.mode === 'image') cancelImageCrop(prev);
      removeCropOverlay(canvas, prev.overlay);
    }
    cropSessionRef.current = beginImageCrop(canvas, image);
    setCropActive(true);
  }, []);

  clearCropSessionRef.current = clearCropSession;
  startImageCropSessionRef.current = startImageCropSession;

  const {
    isSaving,
    lastSaved,
    saveError,
    manualSave,
    clearError,
  } = useAutoSave({
    interval: autoSaveInterval,
    generateThumbnail: generateThumbnails,
    maxRetries: 3,
  });

  const finishPenPath = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    if (penPreviewRef.current) {
      canvas.remove(penPreviewRef.current);
      penPreviewRef.current = null;
    }
    const path = finalizePenPath(
      canvas,
      penPointsRef.current,
      colorRef.current,
      Math.max(1, brushSizeRef.current),
      opacityRef.current
    );
    penPointsRef.current = [];
    skipHistoryRef.current = false;
    if (path) {
      onCanvasChangeRef.current();
      useCanvasStore.getState().setTool('select');
    }
  }, []);

  toolRef.current = selectedTool;
  colorRef.current = selectedColor;
  opacityRef.current = opacity;
  brushSizeRef.current = brushSize;
  gradientToRef.current = gradientToColor;
  zoomRef.current = zoom;
  onZoomChangeRef.current = onZoomChange;
  altHeldRef.current = altHeld;

  const syncPagesToProject = useCallback(
    (nextPages: ProjectPage[], nextActiveId: string, canvasData?: FabricCanvasData) => {
      const project = useProjectStore.getState().currentProject;
      if (!project) return;
      updateProject({
        ...project,
        pages: nextPages,
        activePageId: nextActiveId,
        canvasData: canvasData ?? project.canvasData,
      });
    },
    [updateProject]
  );

  const onCanvasChange = useCallback(() => {
    if (
      !fabricCanvasRef.current ||
      skipHistoryRef.current ||
      applyingHistoryRef.current ||
      // Belt-and-braces: skipHistoryRef belongs to this component, preview does not.
      isPreviewActive()
    ) {
      return;
    }

    const json = fabricCanvasRef.current.toJSON([...LAYER_CUSTOM_PROPS]);
    const updatedCanvasData: FabricCanvasData = {
      version: (json.version as string) || '5.3.0',
      objects: (json.objects as unknown as Record<string, unknown>[]) || [],
      width: fabricCanvasRef.current.getWidth(),
      height: fabricCanvasRef.current.getHeight(),
      background: (json as { background?: string }).background || '#ffffff',
    };

    setCanvasData(updatedCanvasData);
    addToHistory(updatedCanvasData);
    markDirty(true);

    const pageId = activePageIdRef.current;
    if (pageId) {
      const nextPages = replacePageData(pagesRef.current, pageId, updatedCanvasData);
      pagesRef.current = nextPages;
      setPages(nextPages);
      syncPagesToProject(nextPages, pageId, updatedCanvasData);
    }
  }, [setCanvasData, addToHistory, markDirty, syncPagesToProject]);

  onCanvasChangeRef.current = onCanvasChange;

  const loadCanvasDataIntoFabric = useCallback((data: FabricCanvasData) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    applyingHistoryRef.current = true;
    skipHistoryRef.current = true;
    // Before loadFromJSON, or Fabric silently drops video layers (see fabricVideoObject.ts).
    registerVideoObject();
    canvas.loadFromJSON(
      {
        version: data.version || '5.3.0',
        objects: data.objects || [],
        background: data.background || '#ffffff',
      },
      () => {
        if (data.width) canvas.setWidth(data.width);
        if (data.height) canvas.setHeight(data.height);
        canvas.forEachObject((obj) => {
          if (isObjectLocked(obj)) {
            setObjectLocked(obj, true);
          } else {
            obj.selectable = true;
            obj.evented = true;
          }
          obj.setCoords();
        });
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        skipHistoryRef.current = false;
        applyingHistoryRef.current = false;
      }
    );
  }, []);

  /** End live preview (restoring the canvas) before anything reloads or reads it. */
  const stopPreview = useCallback(() => useVideoStore.getState().stop(), []);

  // Preview borrows skipHistoryRef; give back whatever value it had (pen drawing sets it too).
  const skipBeforePreviewRef = useRef(false);
  useVideoPreview({
    canvas: fabricCanvas,
    ready: isCanvasReady,
    setHistorySuppressed: (on) => {
      if (on) {
        skipBeforePreviewRef.current = skipHistoryRef.current;
        skipHistoryRef.current = true;
      } else {
        skipHistoryRef.current = skipBeforePreviewRef.current;
      }
    },
    canEnter: () => !applyingHistoryRef.current,
    artboard: () => ({
      width: fabricCanvasRef.current?.getWidth() || 0,
      height: fabricCanvasRef.current?.getHeight() || 0,
    }),
  });
  useLiveVideoLayers({ canvas: fabricCanvas, ready: isCanvasReady });

  const flushActivePage = useCallback((): ProjectPage[] => {
    // Preview mutates live object transforms. Persisting mid-animation
    // state would corrupt canvasData and pages[active].
    if (isPreviewActive()) return pagesRef.current;
    const canvas = fabricCanvasRef.current;
    const pageId = activePageIdRef.current;
    if (!canvas || !pageId) return pagesRef.current;

    const json = canvas.toJSON([...LAYER_CUSTOM_PROPS]);
    const data: FabricCanvasData = {
      version: (json.version as string) || '5.3.0',
      objects: (json.objects as unknown as Record<string, unknown>[]) || [],
      width: canvas.getWidth(),
      height: canvas.getHeight(),
      background: (json as { background?: string }).background || '#ffffff',
    };
    const next = replacePageData(pagesRef.current, pageId, data);
    pagesRef.current = next;
    setPages(next);
    setCanvasData(data);
    return next;
  }, [setCanvasData]);

  const startPresent = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const flushed = flushActivePage();
    void captureAllPresentSlides({
      canvas,
      pages: flushed.length ? flushed : pagesRef.current,
      activePageId: activePageIdRef.current,
    }).then((slides) => {
      setPresentSlides(slides);
      setPresentOpen(true);
    });
  }, [flushActivePage]);

  const selectPage = useCallback(
    (pageId: string) => {
      if (pageId === activePageIdRef.current) return;
      stopPreview();
      const flushed = flushActivePage();
      const target = getActivePage(flushed, pageId);
      if (!target) return;
      setActivePageId(pageId);
      activePageIdRef.current = pageId;
      loadCanvasDataIntoFabric(target.canvasData);
      setCanvasData(target.canvasData);
      clearHistory();
      syncPagesToProject(flushed, pageId, target.canvasData);
      markDirty(true);
    },
    [
      stopPreview,
      flushActivePage,
      loadCanvasDataIntoFabric,
      setCanvasData,
      clearHistory,
      syncPagesToProject,
      markDirty,
    ]
  );

  const addPage = useCallback(() => {
    stopPreview();
    const project = useProjectStore.getState().currentProject;
    const flushed = flushActivePage();
    const page = createBlankPage(
      project?.width || 1200,
      project?.height || 800,
      undefined,
      flushed.length + 1
    );
    const next = [...flushed, page];
    pagesRef.current = next;
    setPages(next);
    setActivePageId(page.pageId);
    activePageIdRef.current = page.pageId;
    loadCanvasDataIntoFabric(page.canvasData);
    setCanvasData(page.canvasData);
    clearHistory();
    syncPagesToProject(next, page.pageId, page.canvasData);
    markDirty(true);
  }, [
    stopPreview,
    flushActivePage,
    loadCanvasDataIntoFabric,
    setCanvasData,
    clearHistory,
    syncPagesToProject,
    markDirty,
  ]);

  const duplicateActivePage = useCallback(() => {
    stopPreview();
    const flushed = flushActivePage();
    const current = getActivePage(flushed, activePageIdRef.current);
    if (!current) return;
    const copy = duplicatePage(current);
    const idx = flushed.findIndex((p) => p.pageId === current.pageId);
    const next = [
      ...flushed.slice(0, idx + 1),
      copy,
      ...flushed.slice(idx + 1),
    ];
    pagesRef.current = next;
    setPages(next);
    setActivePageId(copy.pageId);
    activePageIdRef.current = copy.pageId;
    loadCanvasDataIntoFabric(copy.canvasData);
    setCanvasData(copy.canvasData);
    clearHistory();
    syncPagesToProject(next, copy.pageId, copy.canvasData);
    markDirty(true);
  }, [
    stopPreview,
    flushActivePage,
    loadCanvasDataIntoFabric,
    setCanvasData,
    clearHistory,
    syncPagesToProject,
    markDirty,
  ]);

  const deleteActivePage = useCallback(() => {
    if (pagesRef.current.length <= 1) return;
    stopPreview();
    const flushed = flushActivePage();
    const idx = flushed.findIndex((p) => p.pageId === activePageIdRef.current);
    if (idx < 0) return;
    const next = flushed.filter((p) => p.pageId !== activePageIdRef.current);
    const fallback = next[Math.max(0, idx - 1)] || next[0];
    pagesRef.current = next;
    setPages(next);
    setActivePageId(fallback.pageId);
    activePageIdRef.current = fallback.pageId;
    loadCanvasDataIntoFabric(fallback.canvasData);
    setCanvasData(fallback.canvasData);
    clearHistory();
    syncPagesToProject(next, fallback.pageId, fallback.canvasData);
    markDirty(true);
  }, [
    stopPreview,
    flushActivePage,
    loadCanvasDataIntoFabric,
    setCanvasData,
    clearHistory,
    syncPagesToProject,
    markDirty,
  ]);

  const renamePage = useCallback(
    (pageId: string, name: string) => {
      const next = pagesRef.current.map((p) =>
        p.pageId === pageId ? { ...p, name } : p
      );
      pagesRef.current = next;
      setPages(next);
      syncPagesToProject(next, activePageIdRef.current);
      markDirty(true);
    },
    [syncPagesToProject, markDirty]
  );

  const moveActivePage = useCallback(
    (pageId: string, delta: -1 | 1) => {
      const flushed = flushActivePage();
      const next = movePage(flushed, pageId, delta);
      if (next === flushed) return;
      pagesRef.current = next;
      setPages(next);
      syncPagesToProject(next, activePageIdRef.current);
      markDirty(true);
    },
    [flushActivePage, syncPagesToProject, markDirty]
  );

  const reorderPages = useCallback(
    (fromId: string, toId: string) => {
      const flushed = flushActivePage();
      const next = reorderPage(flushed, fromId, toId);
      if (next === flushed) return;
      pagesRef.current = next;
      setPages(next);
      syncPagesToProject(next, activePageIdRef.current);
      markDirty(true);
    },
    [flushActivePage, syncPagesToProject, markDirty]
  );

  // Scene commands alias these page handlers so scenes and pages cannot desync.
  const pageHandlersRef = useRef({ addPage, duplicateActivePage, deleteActivePage });
  pageHandlersRef.current = { addPage, duplicateActivePage, deleteActivePage };

  // Scenes follow pages: heal order/membership after any page change or load.
  useEffect(() => {
    if (motionMode) useVideoStore.getState().syncToPages(pages);
  }, [pages, motionMode]);

  useEffect(() => () => useVideoStore.getState().stop(), []);

  // "Animate…" in Properties: open the Motion tab. Design projects never enter the timeline.
  useEffect(() => {
    const onOpen = () => {
      if (studioRef.current !== 'movie') return;
      const video = useVideoStore.getState();
      if (!video.video?.enabled) video.enableMotion();
      setRightTab('motion');
      setRightDockOpen(true);
    };
    window.addEventListener(OPEN_ANIMATION_PANEL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ANIMATION_PANEL_EVENT, onOpen);
  }, []);

  useEffect(() => {
    const onOpen = () => {
      if (studioRef.current !== 'movie') return;
      const video = useVideoStore.getState();
      if (!exportAllowed(layoutModeRef.current)) {
        video.setNotice(MOTION_EXPORT_DESKTOP_COPY);
        return;
      }
      video.stop();
      if (!video.video?.enabled) video.enableMotion();
      setVideoExportOpen(true);
    };
    window.addEventListener(OPEN_VIDEO_EXPORT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_VIDEO_EXPORT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!motionMode && rightTab === 'motion') setRightTab('properties');
  }, [motionMode, rightTab]);

  // Init / re-sync pages when project loads
  useEffect(() => {
    if (!currentProject) return;
    const resolved = resolveProjectForEditor(currentProject);
    pagesRef.current = resolved.pages;
    setPages(resolved.pages);
    setActivePageId(resolved.activePageId);
    activePageIdRef.current = resolved.activePageId;
    setCanvasData(resolved.canvasData);
    markDirty(false);
    if (fabricCanvasRef.current) {
      loadCanvasDataIntoFabric(resolved.canvasData);
    }
    // Persist healed pages if they were out of sync with top-level canvasData
    syncPagesToProject(
      resolved.pages,
      resolved.activePageId,
      resolved.canvasData
    );
  }, [currentProject?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush live Fabric JSON + persist when Close/Back is pressed
  useEffect(() => {
    registerEditorSaveHandler(async () => {
      stopPreview();
      const flushed = flushActivePage();
      const pageId = activePageIdRef.current;
      const liveCanvas = useCanvasStore.getState().canvasData;
      if (!liveCanvas) return false;

      const project = useProjectStore.getState().currentProject;
      if (!project) return false;

      const synced = ensurePagesMatchCanvas(
        {
          ...project,
          pages: flushed.length ? flushed : project.pages,
          activePageId: pageId || project.activePageId,
        },
        liveCanvas
      );
      pagesRef.current = synced.pages;
      setPages(synced.pages);
      syncPagesToProject(synced.pages, synced.activePageId, liveCanvas);
      return manualSave();
    });
    return () => registerEditorSaveHandler(null);
  }, [stopPreview, flushActivePage, syncPagesToProject, manualSave]);

  // Page strip thumbnails (debounced; active page from live canvas)
  useEffect(() => {
    if (!pages.length) {
      setPageThumbs({});
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const next: Record<string, string> = {};
        const liveCanvas = fabricCanvasRef.current;
        for (const page of pages) {
          if (cancelled) return;
          try {
            if (
              page.pageId === activePageIdRef.current &&
              liveCanvas &&
              isCanvasReady
            ) {
              // Mid-preview the live canvas shows an animated frame, not the design.
              if (isPreviewActive()) continue;
              next[page.pageId] = liveCanvas.toDataURL({
                format: 'png',
                multiplier: 0.12,
                enableRetinaScaling: false,
              });
            } else {
              next[page.pageId] = await renderPageToDataUrl(page.canvasData, 0.12);
            }
          } catch {
            /* keep previous / skip */
          }
        }
        if (!cancelled) setPageThumbs(next);
      })();
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [pages, activePageId, isCanvasReady, isDirty]);

  // Apply undo/redo/history jump onto Fabric
  useEffect(() => {
    const onApply = (e: Event) => {
      const data = (e as CustomEvent<FabricCanvasData>).detail;
      // The preview baseline references objects loadFromJSON is about to destroy.
      stopPreview();
      if (data) loadCanvasDataIntoFabric(data);
    };
    window.addEventListener(HISTORY_APPLY_EVENT, onApply);
    return () => window.removeEventListener(HISTORY_APPLY_EVENT, onApply);
  }, [stopPreview, loadCanvasDataIntoFabric]);

  // Initialize Fabric once the canvas element is mounted
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    if (typeof fabric?.Canvas !== 'function') {
      setLoadError('Fabric.js failed to load. Hard-refresh the page and try again.');
      console.error('Fabric namespace invalid:', fabric);
      return;
    }

    let disposed = false;
    const width = canvasData?.width || currentProject?.width || 1200;
    const height = canvasData?.height || currentProject?.height || 800;

    try {
      const fabricCanvas = new fabric.Canvas(el, {
        width,
        height,
        backgroundColor: '#ffffff',
        enableRetinaScaling: true,
        preserveObjectStacking: true,
        selection: true,
      });

      if (disposed) {
        fabricCanvas.dispose();
        return;
      }

      fabricCanvasRef.current = fabricCanvas;
      setFabricCanvas(fabricCanvas);

      // CSS zoom uses transform:scale on .canvas-container — fix object drag pointers
      const uninstallPointerFix = installCssZoomPointerFix(fabricCanvas);

      // If Fabric misses mouseup (common with CSS-scaled canvas), drag stays stuck.
      const onWindowPointerUp = () => {
        window.setTimeout(() => {
          if (disposed) return;
          const c = fabricCanvasRef.current as
            | (fabric.Canvas & {
                _currentTransform?: {
                  target?: fabric.Object & { isMoving?: boolean };
                } | null;
              })
            | null;
          if (!c?._currentTransform) return;
          const t = c._currentTransform;
          if (t.target) {
            t.target.isMoving = false;
            t.target.setCoords();
          }
          c._currentTransform = null;
          c.requestRenderAll();
          if (!skipHistoryRef.current) onCanvasChangeRef.current();
        }, 0);
      };
      window.addEventListener('mouseup', onWindowPointerUp);
      window.addEventListener('pointerup', onWindowPointerUp);
      window.addEventListener('blur', onWindowPointerUp);

      const handleChange = () => {
        if (skipHistoryRef.current) return;
        onCanvasChange();
      };

      const handleTextModified = (opt: fabric.IEvent) => {
        const target = opt.target;
        if (!target) return;
        // Same as Libraries text: after handle-resize, bake scale → fontSize/width
        if (bakeTextObjectScale(target)) {
          fabricCanvas.requestRenderAll();
        }
        handleChange();
      };

      const handleTextEditingEntered = (opt: fabric.IEvent) => {
        const target = opt.target;
        if (!target) return;
        if (bakeTextObjectScale(target)) {
          fabricCanvas.requestRenderAll();
        }
      };

      const handleObjectAdded = (opt: fabric.IEvent) => {
        if (opt.target) ensureObjectUid(opt.target);
      };

      fabricCanvas.on('object:added', handleObjectAdded);
      fabricCanvas.on('object:modified', handleTextModified);
      fabricCanvas.on('text:editing:entered', handleTextEditingEntered);
      fabricCanvas.on('object:removed', handleChange);
      fabricCanvas.on('path:created', handleChange);

      const pointer = (opt: fabric.IEvent<Event>) => {
        const e = opt.e as MouseEvent | undefined;
        // CSS zoom on .canvas-container — map via client rect (not Fabric getPointer alone)
        if (e && typeof e.clientX === 'number') {
          return clientToArtboard(fabricCanvas, e.clientX, e.clientY);
        }
        return fabricCanvas.getPointer(opt.e);
      };

      const syncLassoSvg = (pts: Array<{ x: number; y: number }>) => {
        const el = lassoPolyRef.current;
        if (el) el.setAttribute('points', lassoPointsAttr(pts));
      };

      const getStage = () =>
        stageRef.current ||
        (canvasRef.current?.closest('.ps-stage') as HTMLElement | null);

      const beginPan = (e: Event) => {
        const stage = getStage();
        const me = e as MouseEvent;
        if (!stage) return;
        panSessionRef.current = {
          clientX: me.clientX,
          clientY: me.clientY,
          scrollLeft: stage.scrollLeft,
          scrollTop: stage.scrollTop,
        };
        fabricCanvas.defaultCursor = 'grabbing';
        fabricCanvas.setCursor('grabbing');
      };

      const onMouseDown = (opt: fabric.IEvent<Event>) => {
        // Ignore Fabric-synthesized mouse events during pinch / 2-finger pan
        if (touchGestureActiveRef.current) return;

        const tool = toolRef.current;
        const wantsPan = tool === 'hand' || spacePanRef.current;

        if (wantsPan) {
          opt.e?.preventDefault?.();
          beginPan(opt.e);
          return;
        }

        if (tool === 'eraser') {
          opt.e?.preventDefault?.();
          eraseActiveRef.current = true;
          fabricCanvas.discardActiveObject();
          const p = pointer(opt);
          if (eraseObjectAtPoint(fabricCanvas, p)) {
            eraseDirtyRef.current = true;
          }
          return;
        }

        if (tool === 'zoom') {
          opt.e?.preventDefault?.();
          const me = opt.e as MouseEvent;
          const step = me.altKey || altHeldRef.current ? -10 : 10;
          onZoomChangeRef.current?.(clampZoom(zoomRef.current + step));
          return;
        }

        if (tool === 'gradient') {
          const target = opt.target;
          if (target) {
            fabricCanvas.setActiveObject(target);
            if (
              applyGradientFill(fabricCanvas, 'linear', {
                from: colorRef.current,
                to: gradientToRef.current,
              })
            ) {
              onCanvasChangeRef.current();
            }
          }
          return;
        }

        if (tool === 'bucket') {
          opt.e?.preventDefault?.();
          const target = opt.target || null;
          const result = applyPaintBucket(
            fabricCanvas,
            target,
            colorRef.current,
            opacityRef.current
          );
          if (result.ok) onCanvasChangeRef.current();
          return;
        }

        if (tool === 'eyedropper') {
          opt.e?.preventDefault?.();
          const target = opt.target;
          const hex = sampleColorFromObject(target || null);
          if (hex) {
            useCanvasStore.getState().setSelectedColor(hex);
          }
          return;
        }

        if (tool === 'marquee') {
          opt.e?.preventDefault?.();
          const p = pointer(opt);
          const store = useCanvasStore.getState();
          const additive =
            Boolean((opt.e as MouseEvent)?.shiftKey) || store.additiveSelect;
          const marqueeShape = store.marqueeShape || 'rect';
          if (!additive) fabricCanvas.discardActiveObject();
          const overlay = createMarqueeOverlay(
            {
              left: p.x,
              top: p.y,
              width: 1,
              height: 1,
            },
            marqueeShape
          );
          fabricCanvas.add(overlay);
          drawSessionRef.current = {
            tool: 'marquee',
            originX: p.x,
            originY: p.y,
            shape: overlay,
            additive,
            marqueeShape,
          };
          return;
        }

        if (tool === 'lasso') {
          opt.e?.preventDefault?.();
          const p = pointer(opt);
          const additive =
            Boolean((opt.e as MouseEvent)?.shiftKey) ||
            useCanvasStore.getState().additiveSelect;
          const preferredImage = findImageAtArtboardPoint(fabricCanvas, p);
          if (!additive) fabricCanvas.discardActiveObject();
          lassoPointsRef.current = [{ x: p.x, y: p.y }];
          setLassoActive(true);
          syncLassoSvg(lassoPointsRef.current);
          // Dummy shape keeps the shared drag-session / mouseup path working
          drawSessionRef.current = {
            tool: 'lasso',
            originX: p.x,
            originY: p.y,
            shape: new fabric.Rect({
              left: 0,
              top: 0,
              width: 1,
              height: 1,
              visible: false,
              selectable: false,
              evented: false,
              excludeFromExport: true,
            }),
            additive,
            preferredImage,
          };
          return;
        }

        if (tool === 'crop') {
          opt.e?.preventDefault?.();
          if (cropSessionRef.current) {
            // Allow adjusting existing overlay
            return;
          }
          const target = opt.target;
          if (target && target.type === 'image' && !isCropOverlay(target)) {
            startImageCropSessionRef.current(target as fabric.Image);
            return;
          }
          if (target && !isCropOverlay(target)) {
            return;
          }
          const p = pointer(opt);
          const overlay = createCropOverlay({
            left: p.x,
            top: p.y,
            width: 1,
            height: 1,
          });
          overlay.set({ selectable: false, evented: false });
          fabricCanvas.add(overlay);
          cropSessionRef.current = { mode: 'artboard', overlay };
          setCropActive(true);
          drawSessionRef.current = {
            tool: 'crop',
            originX: p.x,
            originY: p.y,
            shape: overlay,
          };
          return;
        }

        if (tool === 'pen') {
          opt.e?.preventDefault?.();
          const p = pointer(opt);
          const me = opt.e as MouseEvent;
          penPointsRef.current.push({ x: p.x, y: p.y });

          if (penPreviewRef.current) {
            fabricCanvas.remove(penPreviewRef.current);
          }
          const preview = createPenPreview(
            penPointsRef.current,
            colorRef.current,
            Math.max(1, brushSizeRef.current)
          );
          if (preview) {
            fabricCanvas.add(preview);
            penPreviewRef.current = preview;
            fabricCanvas.requestRenderAll();
          }

          if (me.detail >= 2 && penPointsRef.current.length >= 2) {
            // double-click finishes
            if (penPreviewRef.current) {
              fabricCanvas.remove(penPreviewRef.current);
              penPreviewRef.current = null;
            }
            const path = finalizePenPath(
              fabricCanvas,
              penPointsRef.current,
              colorRef.current,
              Math.max(1, brushSizeRef.current),
              opacityRef.current
            );
            penPointsRef.current = [];
            if (path) {
              onCanvasChangeRef.current();
              useCanvasStore.getState().setTool('select');
            }
          }
          return;
        }

        if (
          tool === 'comment' ||
          tool === 'select' ||
          tool === 'draw' ||
          tool === 'pencil'
        ) {
          return;
        }

        // Clicking an existing object should select/edit it, not spawn a new one
        if (opt.target) {
          useCanvasStore.getState().setTool('select');
          return;
        }

        const p = pointer(opt);
        const fill = colorRef.current;
        const fillOpacity = opacityRef.current;

        skipHistoryRef.current = true;
        fabricCanvas.selection = false;
        fabricCanvas.discardActiveObject();

        // text / textbox: handled by installTextDragCreate (pointer-capture)

        if (tool === 'frame') {
          const frame = createFrameObject('roundedRect', {
            left: p.x,
            top: p.y,
            width: 1,
            height: 1,
            selectable: false,
            evented: false,
          });
          fabricCanvas.add(frame);
          drawSessionRef.current = {
            tool,
            originX: p.x,
            originY: p.y,
            shape: frame,
          };
          return;
        }

        if (!isShapeRuntimeTool(tool)) {
          skipHistoryRef.current = false;
          return;
        }

        const shape = createDragShape(
          tool,
          p,
          {
            fill,
            opacity: fillOpacity,
            stroke: fill,
            strokeWidth: isLinearTool(tool) ? brushSizeRef.current : brushSizeRef.current,
            cornerRadius: tool === 'roundedRect' ? 16 : 0,
          },
          { sides: useCanvasStore.getState().polygonSides }
        );

        fabricCanvas.add(shape);
        drawSessionRef.current = {
          tool,
          originX: p.x,
          originY: p.y,
          shape,
        };
      };

      const onMouseMove = (opt: fabric.IEvent<Event>) => {
        const pan = panSessionRef.current;
        if (pan) {
          const stage = getStage();
          const me = opt.e as MouseEvent;
          if (stage) {
            stage.scrollLeft = pan.scrollLeft - (me.clientX - pan.clientX);
            stage.scrollTop = pan.scrollTop - (me.clientY - pan.clientY);
          }
          return;
        }

        if (eraseActiveRef.current && toolRef.current === 'eraser') {
          const p = pointer(opt);
          if (eraseObjectAtPoint(fabricCanvas, p)) {
            eraseDirtyRef.current = true;
          }
          return;
        }

        const session = drawSessionRef.current;
        if (!session?.shape) return;

        const p = pointer(opt);
        const left = Math.min(session.originX, p.x);
        const top = Math.min(session.originY, p.y);
        const w = Math.abs(p.x - session.originX);
        const h = Math.abs(p.y - session.originY);

        if (session.tool === 'lasso') {
          const pts = lassoPointsRef.current;
          const last = pts[pts.length - 1];
          if (!last || Math.hypot(p.x - last.x, p.y - last.y) >= 1.5) {
            pts.push({ x: p.x, y: p.y });
            syncLassoSvg(pts);
          }
          return;
        }

        if (session.tool === 'crop' || session.tool === 'marquee') {
          if (session.tool === 'marquee' && session.marqueeShape === 'ellipse') {
            (session.shape as fabric.Ellipse).set({
              left,
              top,
              rx: Math.max(0.5, w / 2),
              ry: Math.max(0.5, h / 2),
            });
          } else {
            session.shape.set({
              left,
              top,
              width: Math.max(session.tool === 'marquee' ? 1 : 8, w),
              height: Math.max(session.tool === 'marquee' ? 1 : 8, h),
            });
          }
          fabricCanvas.requestRenderAll();
          return;
        }

        if (session.tool === 'frame') {
          if (session.shape.type === 'ellipse') {
            (session.shape as fabric.Ellipse).set({
              left,
              top,
              rx: Math.max(1, w / 2),
              ry: Math.max(1, h / 2),
            });
          } else {
            session.shape.set({ left, top, width: Math.max(1, w), height: Math.max(1, h) });
          }
          fabricCanvas.requestRenderAll();
          return;
        }

        if (!isShapeRuntimeTool(session.tool)) return;

        updateDragShape(
          session.tool as ShapeRuntimeTool,
          session.shape,
          { x: session.originX, y: session.originY },
          p,
          { sides: useCanvasStore.getState().polygonSides }
        );
        fabricCanvas.requestRenderAll();
      };

      const onMouseUp = () => {
        if (panSessionRef.current) {
          panSessionRef.current = null;
          const tool = toolRef.current;
          const cursor =
            tool === 'hand' || spacePanRef.current ? 'grab' : 'default';
          fabricCanvas.defaultCursor = cursor;
          fabricCanvas.setCursor(cursor);
          return;
        }

        if (eraseActiveRef.current) {
          eraseActiveRef.current = false;
          if (eraseDirtyRef.current) {
            eraseDirtyRef.current = false;
            onCanvasChangeRef.current();
          }
          return;
        }

        // Ensure Fabric didn't leave a drag active (pointer bugs / lost mouseup)
        const interactive = fabricCanvas as fabric.Canvas & {
          _currentTransform?: { target?: fabric.Object | null } | null;
        };
        if (interactive._currentTransform) {
          const t = interactive._currentTransform;
          if (t?.target) {
            (t.target as fabric.Object & { isMoving?: boolean }).isMoving = false;
            t.target.setCoords();
          }
          interactive._currentTransform = null;
        }

        const session = drawSessionRef.current;
        if (!session?.shape) return;

        // Lasso first — dummy shape is not on the canvas; avoid setCoords/bounds on it
        if (session.tool === 'lasso') {
          const additive = Boolean(session.additive);
          const preferredImage = session.preferredImage || null;
          const pts = lassoPointsRef.current.slice();
          drawSessionRef.current = null;
          lassoPointsRef.current = [];
          setLassoActive(false);
          syncLassoSvg([]);
          useCanvasStore.getState().setTool('select');
          fabricCanvas.selection = true;
          fabricCanvas.forEachObject((obj) => {
            if (isGuideObject(obj)) {
              obj.selectable = true;
              obj.evented = true;
              return;
            }
            if (!isMarqueeOverlay(obj) && !isLassoOverlay(obj) && !isCropOverlay(obj)) {
              if (!isObjectLocked(obj)) {
                obj.selectable = true;
                obj.evented = true;
              }
            }
          });
          fabricCanvas.requestRenderAll();
          if (pts.length >= 3) {
            setUploadNote('Applying lasso…');
            void applyLasso(fabricCanvas, pts, { additive, preferredImage })
              .then((result) => {
                if (result.mode === 'cutout') {
                  setUploadNote('Lasso cutout applied — trimmed to the selected region');
                  onCanvasChangeRef.current();
                } else if (result.mode === 'select') {
                  setUploadNote(`Selected ${result.count} object(s)`);
                  onCanvasChangeRef.current();
                } else {
                  setUploadNote(result.reason || 'Lasso found nothing — draw on a photo');
                }
              })
              .catch((err) => {
                console.error(err);
                setUploadNote(
                  err instanceof Error ? err.message : 'Lasso failed'
                );
              });
          } else {
            setUploadNote('Draw a larger loop before releasing');
          }
          return;
        }

        let shape = session.shape;
        shape.setCoords();
        const bounds = shape.getBoundingRect(true);
        const linear =
          isShapeRuntimeTool(session.tool) && isLinearTool(session.tool);
        const ellipse = shape.type === 'ellipse' ? (shape as fabric.Ellipse) : null;
        const shapeW = Math.max(
          bounds.width,
          (shape.width || 0) * (shape.scaleX || 1),
          ellipse ? (ellipse.rx || 0) * 2 : 0
        );
        const shapeH = Math.max(
          bounds.height,
          (shape.height || 0) * (shape.scaleY || 1),
          ellipse ? (ellipse.ry || 0) * 2 : 0
        );
        const line = linear ? (shape as fabric.Line) : null;
        const dragDist = line
          ? Math.hypot((line.x2 || 0) - (line.x1 || 0), (line.y2 || 0) - (line.y1 || 0))
          : 0;
        const tooSmall = linear
          ? dragDist < 4
          : session.tool === 'textbox'
            ? shapeW < 24
            : session.tool === 'crop'
              ? shapeW < 16 || shapeH < 16
              : shapeW < 4 && shapeH < 4;

        if (session.tool === 'marquee') {
          const additive = Boolean(session.additive);
          const mShape = session.marqueeShape || 'rect';
          drawSessionRef.current = null;
          const mLeft = shape.left || 0;
          const mTop = shape.top || 0;
          let mW: number;
          let mH: number;
          if (mShape === 'ellipse') {
            const el = shape as fabric.Ellipse;
            mW = Math.max(1, (el.rx || 0) * 2 * (el.scaleX || 1));
            mH = Math.max(1, (el.ry || 0) * 2 * (el.scaleY || 1));
          } else {
            mW = Math.max(1, (shape.width || 0) * (shape.scaleX || 1));
            mH = Math.max(1, (shape.height || 0) * (shape.scaleY || 1));
          }
          fabricCanvas.remove(shape);
          if (mW >= 4 && mH >= 4) {
            selectObjectsInMarquee(
              fabricCanvas,
              {
                left: mLeft,
                top: mTop,
                width: mW,
                height: mH,
              },
              { additive, shape: mShape }
            );
          }
          useCanvasStore.getState().setTool('select');
          fabricCanvas.selection = true;
          fabricCanvas.forEachObject((obj) => {
            if (isGuideObject(obj)) {
              obj.selectable = true;
              obj.evented = true;
              return;
            }
            if (!isMarqueeOverlay(obj) && !isCropOverlay(obj)) {
              if (!isObjectLocked(obj)) {
                obj.selectable = true;
                obj.evented = true;
              }
            }
          });
          fabricCanvas.requestRenderAll();
          return;
        }

        if (session.tool === 'crop') {
          drawSessionRef.current = null;
          if (tooSmall) {
            fabricCanvas.remove(shape);
            cropSessionRef.current = null;
            setCropActive(false);
          } else {
            shape.set({ selectable: true, evented: true });
            shape.setCoords();
            fabricCanvas.setActiveObject(shape);
            fabricCanvas.requestRenderAll();
          }
          return;
        }

        if (tooSmall) {
          fabricCanvas.remove(shape);
        } else if (session.tool === 'arrow' && shape.type === 'line') {
          shape = finalizeArrow(fabricCanvas, shape as fabric.Line, {
            fill: colorRef.current,
            opacity: opacityRef.current,
            strokeWidth: brushSizeRef.current,
          });
          shape.setCoords();
          fabricCanvas.setActiveObject(shape);
        } else if (session.tool === 'polygon' || session.tool === 'star') {
          shape = finalizeDragPolygon(
            fabricCanvas,
            shape as fabric.Polygon,
            session.tool,
            { sides: useCanvasStore.getState().polygonSides }
          );
          shape.setCoords();
          fabricCanvas.setActiveObject(shape);
        } else {
          shape.set({
            selectable: true,
            evented: true,
            objectCaching: true,
          });
          shape.setCoords();
          fabricCanvas.setActiveObject(shape);
        }

        drawSessionRef.current = null;
        skipHistoryRef.current = false;
        useCanvasStore.getState().setTool('select');
        fabricCanvas.selection = true;
        fabricCanvas.forEachObject((obj) => {
          obj.selectable = true;
          obj.evented = true;
        });
        fabricCanvas.requestRenderAll();
        onCanvasChangeRef.current();
      };

      fabricCanvas.on('mouse:down', onMouseDown);
      fabricCanvas.on('mouse:move', onMouseMove);
      fabricCanvas.on('mouse:up', onMouseUp);

      const initial = useCanvasStore.getState().canvasData;
      // Prevent object:removed / load side-effects from wiping store mid-load
      skipHistoryRef.current = true;
      applyingHistoryRef.current = true;
      if (initial?.objects && initial.objects.length > 0) {
        registerVideoObject();
        fabricCanvas.loadFromJSON(
          {
            version: initial.version || '5.3.0',
            objects: initial.objects,
            background: initial.background || '#ffffff',
          },
          () => {
            if (!disposed) {
              if (initial.width) fabricCanvas.setWidth(initial.width);
              if (initial.height) fabricCanvas.setHeight(initial.height);
              fabricCanvas.forEachObject((obj) => {
                obj.selectable = true;
                obj.evented = true;
                obj.setCoords();
              });
              fabricCanvas.selection = true;
              fabricCanvas.renderAll();
              setIsCanvasReady(true);
              setLoadError(null);
            }
            skipHistoryRef.current = false;
            applyingHistoryRef.current = false;
          }
        );
      } else {
        fabricCanvas.selection = true;
        fabricCanvas.renderAll();
        setIsCanvasReady(true);
        setLoadError(null);
        skipHistoryRef.current = false;
        applyingHistoryRef.current = false;
      }

      return () => {
        disposed = true;
        uninstallPointerFix();
        window.removeEventListener('mouseup', onWindowPointerUp);
        window.removeEventListener('pointerup', onWindowPointerUp);
        window.removeEventListener('blur', onWindowPointerUp);
        fabricCanvas.off('object:added', handleObjectAdded);
        fabricCanvas.off('object:modified', handleTextModified);
        fabricCanvas.off('text:editing:entered', handleTextEditingEntered);
        fabricCanvas.off('object:removed', handleChange);
        fabricCanvas.off('path:created', handleChange);
        fabricCanvas.off('mouse:down', onMouseDown);
        fabricCanvas.off('mouse:move', onMouseMove);
        fabricCanvas.off('mouse:up', onMouseUp);
        fabricCanvas.dispose();
        fabricCanvasRef.current = null;
        setFabricCanvas(null);
      };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to initialize canvas';
      setLoadError(message);
      console.error('Canvas initialization error:', error);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleInsertElement = useCallback(
    (element: DesignElement) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      canvas.skipTargetFind = false;
      canvas.selection = true;
      void insertDesignElement(canvas, element).then(() => {
        canvas.forEachObject((obj) => {
          obj.selectable = true;
          obj.evented = true;
        });
        onCanvasChange();
        setTool('select');
      });
    },
    [onCanvasChange, setTool]
  );

  const handleApplyTemplate = useCallback(
    async (templateId: string) => {
      const canvas = fabricCanvasRef.current;
      const tpl = TEMPLATE_CATALOG.find((t) => t.templateId === templateId);
      if (!canvas || !tpl) return;

      skipHistoryRef.current = true;
      await applyTemplateContent(canvas, tpl.defaultContent);

      // Keep project / overlays / rulers in sync with the new artboard size
      const project = useProjectStore.getState().currentProject;
      if (project) {
        updateProject({
          ...project,
          width: tpl.width,
          height: tpl.height,
        });
      }

      canvas.calcOffset();
      skipHistoryRef.current = false;
      onCanvasChange();
      setTool('select');

      // Fit large templates in the stage (CSS zoom) so drag/pointer stay usable
      const stage = stageRef.current;
      if (stage && onZoomChangeRef.current) {
        const pad = 48;
        const fit = Math.min(
          ((stage.clientWidth - pad) / tpl.width) * 100,
          ((stage.clientHeight - pad) / tpl.height) * 100,
          100
        );
        onZoomChangeRef.current(clampZoom(fit));
      }
    },
    [onCanvasChange, setTool, updateProject]
  );

  const placeImageFromUrl = useCallback(
    async (url: string) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return false;

      try {
        const img = await loadFabricImage(url);
        const active = canvas.getActiveObject();
        if (active && isFrameObject(active)) {
          fillFrameWithImage(canvas, active, img);
        } else {
          placeFabricImage(canvas, img);
        }
        onCanvasChange();
        setTool('select');
        return true;
      } catch (err) {
        console.error('Failed to place image', err);
        setUploadNote('Could not place image on the artboard');
        return false;
      }
    },
    [onCanvasChange, setTool]
  );

  const handleUploadImage = useCallback(
    async (file: File) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      setUploadBusy(true);
      setUploadNote(null);
      try {
        // Place from a local object URL first — reliable even when Storage CORS blocks remote URLs
        const localUrl = URL.createObjectURL(file);
        const placed = await placeImageFromUrl(localUrl);

        const result = await uploadProjectImage({ file, projectId });
        // Keep a durable URL for re-insert (data URL or Storage); revoke ephemeral blob later
        setRecentUploads((prev) =>
          [
            {
              id: result.path || `${Date.now()}_${result.name}`,
              name: result.name,
              url: result.url,
              source: result.source,
            },
            ...prev.filter((u) => u.url !== result.url),
          ].slice(0, 16)
        );

        // If blob place failed, retry with the durable upload URL / data URL
        if (!placed) {
          await placeImageFromUrl(result.url);
        } else {
          // Blob URL can be revoked after Fabric has decoded pixels into its element
          window.setTimeout(() => URL.revokeObjectURL(localUrl), 30_000);
        }

        setUploadNote(
          result.source === 'storage'
            ? 'Uploaded to cloud Storage'
            : 'Placed locally (sign in to sync to Storage)'
        );
      } catch (err) {
        setUploadNote(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploadBusy(false);
      }
    },
    [placeImageFromUrl, projectId]
  );

  const handleUploadVideo = useCallback(
    async (file: File) => {
      if (!fabricCanvasRef.current) return;
      stopPreview();
      setUploadBusy(true);
      setUploadNote('Reading video…');
      try {
        const imported = await uploadProjectVideo({ file, projectId });
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const posterSrc = URL.createObjectURL(imported.posterBlob);
        const poster = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('Could not read the video’s first frame'));
          img.src = posterSrc;
        }).finally(() => URL.revokeObjectURL(posterSrc));
        // The live video layer manager attaches playback on object:added.
        placeFabricImage(canvas, createVideoObject(imported.clip, poster, imported.posterUrl));
        onCanvasChange();
        setTool('select');
        setUploadNote(
          imported.backedUp
            ? 'Video placed and uploaded to cloud Storage'
            : 'Video placed — stored in this browser only until you sign in'
        );
      } catch (err) {
        setUploadNote(err instanceof Error ? err.message : 'Video import failed');
      } finally {
        setUploadBusy(false);
      }
    },
    [onCanvasChange, projectId, setTool, stopPreview]
  );

  // Type tool: pointer-capture drag so width/height follow the cursor reliably
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !isCanvasReady) return;
    return installTextDragCreate({
      canvas,
      getTool: () => toolRef.current,
      getStyle: () => ({
        fill: colorRef.current,
        opacity: opacityRef.current,
      }),
      onCommitted: () => onCanvasChangeRef.current(),
      onSwitchToSelect: () => {
        useCanvasStore.getState().setTool('select');
        canvas.selection = true;
        canvas.forEachObject((obj) => {
          if (!isObjectLocked(obj)) {
            obj.selectable = true;
            obj.evented = true;
          }
        });
      },
    });
  }, [isCanvasReady]);

  // Drag-and-drop images onto the artboard stage
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !isCanvasReady) return;

    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      stage.classList.add('ps-stage-drop-active');
    };
    const onDragLeave = (e: DragEvent) => {
      if (e.relatedTarget && stage.contains(e.relatedTarget as Node)) return;
      stage.classList.remove('ps-stage-drop-active');
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      stage.classList.remove('ps-stage-drop-active');
      const files = Array.from(e.dataTransfer?.files || []);
      const video = files.find((f) => f.type.startsWith('video/'));
      const image = files.find((f) => f.type.startsWith('image/'));
      if (image) void handleUploadImage(image);
      else if (video) void handleUploadVideo(video);
    };

    stage.addEventListener('dragover', onDragOver);
    stage.addEventListener('dragleave', onDragLeave);
    stage.addEventListener('drop', onDrop);
    return () => {
      stage.removeEventListener('dragover', onDragOver);
      stage.removeEventListener('dragleave', onDragLeave);
      stage.removeEventListener('drop', onDrop);
      stage.classList.remove('ps-stage-drop-active');
    };
  }, [isCanvasReady, handleUploadImage, handleUploadVideo]);

  // Tool mode updates — keep objects clickable in select mode
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !isCanvasReady) return;

    const isPencil = selectedTool === 'pencil';
    const isDraw = selectedTool === 'draw' || isPencil;
    const isPen = selectedTool === 'pen';
    const isShape = isShapeRuntimeTool(selectedTool);
    const isCreate =
      isShape ||
      selectedTool === 'text' ||
      selectedTool === 'textbox' ||
      selectedTool === 'frame';
    const isHand = selectedTool === 'hand' || spaceHeld;
    const isEraser = selectedTool === 'eraser';
    const isZoom = selectedTool === 'zoom';
    const isGradient = selectedTool === 'gradient';
    const isBucket = selectedTool === 'bucket';
    const isEyedropper = selectedTool === 'eyedropper';
    const isCrop = selectedTool === 'crop';
    const isMarquee = selectedTool === 'marquee';
    const isLasso = selectedTool === 'lasso';
    const isComment = selectedTool === 'comment';
    const isSelect = selectedTool === 'select';

    // Don't interrupt in-progress text editing
    const active = canvas.getActiveObject() as fabric.IText | null;
    const isEditingText = Boolean(active && 'isEditing' in active && active.isEditing);

    canvas.isDrawingMode = isDraw;
    if (isDraw) {
      const PencilBrush = (
        fabric as unknown as {
          PencilBrush?: new (c: fabric.Canvas) => fabric.BaseBrush;
        }
      ).PencilBrush;
      if (PencilBrush) {
        const brush = new PencilBrush(canvas) as fabric.BaseBrush & {
          decimate?: number;
          color?: string;
          width?: number;
        };
        brush.decimate = isPencil ? 8 : 0.5;
        canvas.freeDrawingBrush = brush;
      }
    }
    canvas.selection =
      (isSelect &&
        !isHand &&
        !isZoom &&
        !isComment &&
        !isPen &&
        !isCrop &&
        !isMarquee &&
        !isLasso) ||
      isEditingText ||
      (isCrop && cropActive);
    const zoomCursor = altHeld ? 'zoom-out' : 'zoom-in';
    canvas.defaultCursor = isHand
      ? 'grab'
      : isZoom
        ? zoomCursor
        : isGradient || isEyedropper || isBucket
          ? 'copy'
          : isComment
            ? 'cell'
            : isCrop || isMarquee || isLasso
              ? 'crosshair'
              : isEraser || isCreate || isDraw || isPen
                ? 'crosshair'
                : 'default';
    canvas.hoverCursor = isHand
      ? 'grab'
      : isZoom
        ? zoomCursor
        : isGradient || isEyedropper || isBucket
          ? 'copy'
          : isSelect
            ? 'move'
            : 'crosshair';
    canvas.skipTargetFind =
      isDraw ||
      isPen ||
      isHand ||
      isEraser ||
      isZoom ||
      isComment ||
      isMarquee ||
      isLasso ||
      (isCreate && !isEditingText);

    // Hit-test for eyedropper / gradient / bucket / crop (pick image)
    if (isGradient || isEyedropper || isBucket || (isCrop && !cropActive)) {
      canvas.skipTargetFind = false;
    }

    canvas.forEachObject((obj) => {
      if (isCropOverlay(obj)) {
        obj.selectable = isCrop;
        obj.evented = isCrop;
        return;
      }
      if (isObjectLocked(obj)) {
        obj.selectable = false;
        obj.evented = isGradient || isEyedropper || isBucket;
        return;
      }
      if (isGradient || isEyedropper || isBucket) {
        obj.selectable = false;
        obj.evented = true;
      } else if (isCrop && cropActive && cropSessionRef.current?.mode === 'image') {
        const target = cropSessionRef.current.target;
        obj.selectable = false;
        obj.evented = false;
        if (obj === target) {
          obj.selectable = false;
          obj.evented = false;
        }
      } else if (isCrop && !cropActive) {
        obj.selectable = false;
        obj.evented = obj.type === 'image';
      } else if (
        isHand ||
        isEraser ||
        isZoom ||
        isDraw ||
        isPen ||
        isComment ||
        isMarquee ||
        isLasso ||
        (isCreate && !isEditingText)
      ) {
        obj.selectable = false;
        obj.evented = false;
      } else {
        obj.selectable = isSelect;
        obj.evented = true;
      }
    });

    if (isDraw && canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.color = selectedColor;
      canvas.freeDrawingBrush.width = brushSize;
    }

    if (!isSelect && !isEditingText && !isPen && !isCrop && !isMarquee) {
      canvas.discardActiveObject();
    }

    // Leaving pen tool cancels in-progress path
    if (!isPen && penPointsRef.current.length) {
      if (penPreviewRef.current) {
        canvas.remove(penPreviewRef.current);
        penPreviewRef.current = null;
      }
      penPointsRef.current = [];
    }

    // Leaving crop cancels session
    if (!isCrop && cropSessionRef.current) {
      clearCropSessionRef.current();
    }

    canvas.requestRenderAll();
  }, [selectedTool, selectedColor, brushSize, isCanvasReady, spaceHeld, altHeld, cropActive]);

  // Entering crop with an image already selected
  useEffect(() => {
    if (!isCanvasReady || selectedTool !== 'crop' || cropActive) return;
    const canvas = fabricCanvasRef.current;
    const active = canvas?.getActiveObject();
    if (active && active.type === 'image' && !isCropOverlay(active)) {
      startImageCropSession(active as fabric.Image);
    }
  }, [selectedTool, isCanvasReady, cropActive, startImageCropSession]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        void requestEditorSave();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        setExportOpen(true);
      }
      if (e.key === '?' || ((e.ctrlKey || e.metaKey) && e.key === '/')) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      if (e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key === 'Enter')) {
        e.preventDefault();
        startPresent();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        setRulersVisible((v) => !v);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === '\\' || e.code === 'Backslash')) {
        e.preventDefault();
        if (e.shiftKey) {
          setRightDockOpen((v) => !v);
        } else {
          setLeftDockOpen((v) => !v);
        }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        onZoomChange?.(100);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const ok = e.shiftKey ? ungroupSelection(canvas) : groupSelection(canvas);
        if (ok) onCanvasChange();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && !e.shiftKey) {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        copySelection(canvas);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x' && !e.shiftKey) {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        if (cutSelection(canvas)) onCanvasChange();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v' && !e.shiftKey) {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        void pasteClipboard(canvas).then((ok) => {
          if (ok) onCanvasChange();
        });
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd' && !e.shiftKey) {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        void duplicateSelection(canvas).then((ok) => {
          if (ok) onCanvasChange();
        });
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a' && !e.shiftKey) {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        selectAllObjects(canvas);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === ']' || e.key === '[')) {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        const dir =
          e.key === ']'
            ? e.shiftKey
              ? 'front'
              : 'forward'
            : e.shiftKey
              ? 'back'
              : 'backward';
        if (arrangeSelection(canvas, dir)) onCanvasChange();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        useCanvasStore.getState().undo();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'y' || (e.key === 'z' && e.shiftKey))
      ) {
        e.preventDefault();
        useCanvasStore.getState().redo();
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const canvas = fabricCanvasRef.current;
        const activeObj = canvas?.getActiveObject() as fabric.IText | undefined;
        if (activeObj && 'isEditing' in activeObj && activeObj.isEditing) {
          return; // typing in text box
        }
        const active = canvas?.getActiveObjects();
        if (canvas && active && active.length > 0) {
          e.preventDefault();
          const removable = active.filter((obj) => !isObjectLocked(obj));
          if (!removable.length) return;
          removable.forEach((obj) => canvas.remove(obj));
          canvas.discardActiveObject();
          canvas.requestRenderAll();
          onCanvasChange();
        }
      }
      // Opacity shortcuts: 0=100%, 1=10% … 9=90%; type two digits quickly for 15%, 25%, etc.
      if (
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        e.key >= '0' &&
        e.key <= '9'
      ) {
        const canvas = fabricCanvasRef.current;
        const activeObj = canvas?.getActiveObject() as fabric.IText | undefined;
        if (activeObj && 'isEditing' in activeObj && activeObj.isEditing) {
          return;
        }
        e.preventDefault();
        const now = Date.now();
        const chord = opacityChordRef.current;
        let opacity = 1;
        if (chord && now - chord.at < 500) {
          const two = (chord.digits + e.key).slice(-2);
          opacity = Math.min(1, Math.max(0, Number(two) / 100));
          opacityChordRef.current = null;
        } else if (e.key === '0') {
          opacity = 1;
          opacityChordRef.current = { digits: '0', at: now };
        } else {
          opacity = Number(e.key) / 10;
          opacityChordRef.current = { digits: e.key, at: now };
        }

        if (canvas?.getActiveObject()) {
          canvas.getActiveObjects().forEach((obj) => {
            if (!isObjectLocked(obj) && !isGuideObject(obj)) {
              obj.set('opacity', opacity);
            }
          });
          canvas.requestRenderAll();
          onCanvasChange();
        } else {
          useCanvasStore.getState().setOpacity(opacity);
        }
        return;
      }

      if (
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
      ) {
        const canvas = fabricCanvasRef.current;
        const activeObj = canvas?.getActiveObject() as fabric.IText | undefined;
        if (activeObj && 'isEditing' in activeObj && activeObj.isEditing) {
          return;
        }
        if (!canvas?.getActiveObject()) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        if (nudgeSelection(canvas, dx, dy)) onCanvasChange();
        return;
      }
      // Space is normally the pan modifier; Movie Studio repurposes it for playback.
      if (e.code === 'Space' && studioRef.current === 'movie' && useVideoStore.getState().video?.enabled) {
        const activeObj = fabricCanvasRef.current?.getActiveObject() as fabric.IText | undefined;
        if (!(activeObj && 'isEditing' in activeObj && activeObj.isEditing)) {
          e.preventDefault();
          if (!e.repeat) useVideoStore.getState().togglePlay();
          return;
        }
      }
      // Motion transport. These keys belong to tools otherwise (L is lasso);
      // motion mode borrows them because the timeline needs shuttle keys.
      if (
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        studioRef.current === 'movie' &&
        useVideoStore.getState().video?.enabled
      ) {
        const action = motionShortcutFor({ code: e.code, shiftKey: e.shiftKey, repeat: e.repeat });
        const editing = fabricCanvasRef.current?.getActiveObject() as fabric.IText | undefined;
        if (action && !(editing && 'isEditing' in editing && editing.isEditing)) {
          e.preventDefault();
          const store = useVideoStore.getState();
          const frameMs = 1000 / (store.video?.exportSettings.fps || 30);
          if (action.kind === 'play') store.play();
          else if (action.kind === 'pause') store.pause();
          else if (action.kind === 'step') store.seek(store.currentTimeMs + action.deltaFrames * frameMs);
          else if (store.video) {
            const canvas = fabricCanvasRef.current;
            const obj = canvas?.getActiveObject();
            if (canvas && isVideoObject(obj)) {
              obj.set('craftaraVideo', trimClipBy(obj.craftaraVideo, action.edge, action.shorten, frameMs));
              canvas.requestRenderAll();
              onCanvasChange();
              window.dispatchEvent(new CustomEvent(VIDEO_CLIP_CHANGED_EVENT, { detail: { obj } }));
            } else {
              const pageId = useProjectStore.getState().currentProject?.activePageId;
              const scene = pageId ? store.video.scenes.find((s) => s.pageId === pageId) : undefined;
              if (pageId && scene) {
                store.setSceneDuration(pageId, trimSceneBy(scene.durationMs, action.edge, action.shorten, frameMs));
              }
            }
          }
          return;
        }
      }
      if (e.code === 'Space') {
        e.preventDefault();
        if (!e.repeat) {
          spacePanRef.current = true;
          setSpaceHeld(true);
        }
        return;
      }
      if (e.key === 'Alt') {
        altHeldRef.current = true;
        setAltHeld(true);
      }
      if (e.key === 'Enter' && useCanvasStore.getState().selectedTool === 'pen') {
        e.preventDefault();
        finishPenPath();
        return;
      }
      if (e.key === 'Enter' && useCanvasStore.getState().selectedTool === 'crop') {
        e.preventDefault();
        clearCropSessionRef.current({ apply: true });
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        const canvas = fabricCanvasRef.current;
        if (cropSessionRef.current) {
          clearCropSessionRef.current();
          setTool('select');
          return;
        }
        if (penPreviewRef.current && canvas) {
          canvas.remove(penPreviewRef.current);
          penPreviewRef.current = null;
        }
        penPointsRef.current = [];
        lassoPointsRef.current = [];
        setLassoActive(false);
        lassoPolyRef.current?.setAttribute('points', '');
        drawSessionRef.current = null;
        panSessionRef.current = null;
        eraseActiveRef.current = false;
        if (canvas) {
          // Hard-cancel stuck drag/transform + selection (CSS-zoom can leave
          // Fabric's _currentTransform active so the object keeps following).
          forceReleaseCanvas(canvas);
        }
        setTool('select');
        return;
      }

      const shortcut = e.key.toLowerCase();
      if (!e.ctrlKey && !e.metaKey && !e.altKey && shortcut === 'u') {
        e.preventDefault();
        const state = useCanvasStore.getState();
        if (isShapeRuntimeTool(state.selectedTool)) {
          setTool(nextShapeTool(state.selectedTool));
        } else {
          setTool(state.lastShapeTool);
        }
        return;
      }
      if (!e.ctrlKey && !e.metaKey && !e.altKey && shortcut === 't') {
        e.preventDefault();
        const state = useCanvasStore.getState();
        if (isTypeRuntimeTool(state.selectedTool)) {
          setTool(nextTypeTool(state.selectedTool));
        } else {
          setTool(state.lastTypeTool);
        }
        return;
      }
      if (!e.ctrlKey && !e.metaKey && !e.altKey && shortcut === 'm') {
        e.preventDefault();
        const state = useCanvasStore.getState();
        if (state.selectedTool === 'marquee') {
          state.setMarqueeShape(
            state.marqueeShape === 'rect' ? 'ellipse' : 'rect'
          );
        } else {
          setTool('marquee');
        }
        return;
      }
      if (!e.ctrlKey && !e.metaKey && !e.altKey && TOOL_SHORTCUTS[shortcut]) {
        e.preventDefault();
        setTool(TOOL_SHORTCUTS[shortcut]);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spacePanRef.current = false;
        panSessionRef.current = null;
        setSpaceHeld(false);
      }
      if (e.key === 'Alt') {
        altHeldRef.current = false;
        setAltHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [manualSave, onCanvasChange, setTool, onZoomChange, finishPenPath, startPresent]);

  // Menu / external "Export" + Present + View → Rulers + artboard resize
  useEffect(() => {
    const open = () => setExportOpen(true);
    const present = () => startPresent();
    const toggleRulers = () => setRulersVisible((v) => !v);
    const toggleLeftDock = () => {
      if (layoutModeRef.current === 'phone') {
        setMobileSheet((s) => (s === 'libraries' ? null : 'libraries'));
      } else {
        setLeftDockOpen((v) => !v);
      }
    };
    const toggleRightDock = () => {
      if (layoutModeRef.current === 'phone') {
        setMobileSheet((s) => (s === 'inspector' ? null : 'inspector'));
      } else {
        setRightDockOpen((v) => !v);
      }
    };
    const shortcuts = () => setShortcutsOpen(true);
    const onResizeArtboard = (ev: Event) => {
      const detail = (ev as CustomEvent<{ width: number; height: number }>).detail;
      const canvas = fabricCanvasRef.current;
      if (!canvas || !detail?.width || !detail?.height) return;
      const size = resizeArtboard(canvas, detail.width, detail.height);
      const project = useProjectStore.getState().currentProject;
      if (project) {
        const { pages, activePageId } = normalizeProjectPages(project);
        const nextPages = pages.map((p) => ({
          ...p,
          canvasData: {
            ...p.canvasData,
            width: size.width,
            height: size.height,
          },
        }));
        updateProject({
          ...project,
          width: size.width,
          height: size.height,
          pages: nextPages,
          activePageId,
        });
      }
      onCanvasChangeRef.current();
    };
    const openPhotoStudio = () => {
      setLeftTab('photo-studio');
      if (layoutModeRef.current === 'phone') {
        setMobileSheet('libraries');
      } else {
        setLeftDockOpen(true);
      }
    };
    window.addEventListener(OPEN_EXPORT_EVENT, open);
    window.addEventListener(OPEN_PRESENT_EVENT, present);
    window.addEventListener(OPEN_SHORTCUTS_EVENT, shortcuts);
    window.addEventListener(TOGGLE_RULERS_EVENT, toggleRulers);
    window.addEventListener(TOGGLE_LEFT_DOCK_EVENT, toggleLeftDock);
    window.addEventListener(TOGGLE_RIGHT_DOCK_EVENT, toggleRightDock);
    window.addEventListener(RESIZE_ARTBOARD_EVENT, onResizeArtboard);
    window.addEventListener(OPEN_PHOTO_STUDIO_EVENT, openPhotoStudio);
    return () => {
      window.removeEventListener(OPEN_EXPORT_EVENT, open);
      window.removeEventListener(OPEN_PRESENT_EVENT, present);
      window.removeEventListener(OPEN_SHORTCUTS_EVENT, shortcuts);
      window.removeEventListener(TOGGLE_RULERS_EVENT, toggleRulers);
      window.removeEventListener(TOGGLE_LEFT_DOCK_EVENT, toggleLeftDock);
      window.removeEventListener(TOGGLE_RIGHT_DOCK_EVENT, toggleRightDock);
      window.removeEventListener(RESIZE_ARTBOARD_EVENT, onResizeArtboard);
      window.removeEventListener(OPEN_PHOTO_STUDIO_EVENT, openPhotoStudio);
    };
  }, [startPresent, updateProject]);

  // Menubar File / Edit / Object / View commands
  useEffect(() => {
    const canvasOf = () => fabricCanvasRef.current;
    /** Motion mode only: block scene-add past the scene / length / doc-size caps. */
    const sceneBudgetAllows = (): boolean => {
      const video = useVideoStore.getState();
      if (!video.video?.enabled) return true;
      const project = useProjectStore.getState().currentProject;
      const result = assertSceneBudget(
        video.video,
        project ? estimateProjectDocBytes(project) : 0
      );
      if (!result.ok) video.setNotice(result.reason ?? null);
      return result.ok;
    };
    const after = (ok: boolean) => {
      if (ok) onCanvasChangeRef.current();
    };

    registerEditorCommands({
      save: () => void requestEditorSave(),
      export: () => setExportOpen(true),
      present: () => startPresent(),
      undo: () => useCanvasStore.getState().undo(),
      redo: () => useCanvasStore.getState().redo(),
      cut: () => {
        const canvas = canvasOf();
        if (canvas) after(cutSelection(canvas));
      },
      copy: () => {
        const canvas = canvasOf();
        if (canvas) copySelection(canvas);
      },
      paste: () => {
        const canvas = canvasOf();
        if (!canvas) return;
        void pasteClipboard(canvas).then(after);
      },
      duplicate: () => {
        const canvas = canvasOf();
        if (!canvas) return;
        void duplicateSelection(canvas).then(after);
      },
      'select-all': () => {
        const canvas = canvasOf();
        if (canvas) selectAllObjects(canvas);
      },
      delete: () => {
        const canvas = canvasOf();
        if (!canvas) return;
        const active = canvas.getActiveObjects();
        const removable = active.filter((obj) => !isObjectLocked(obj));
        if (!removable.length) return;
        removable.forEach((obj) => canvas.remove(obj));
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        after(true);
      },
      group: () => {
        const canvas = canvasOf();
        if (canvas) after(groupSelection(canvas));
      },
      ungroup: () => {
        const canvas = canvasOf();
        if (canvas) after(ungroupSelection(canvas));
      },
      'bring-front': () => {
        const canvas = canvasOf();
        if (canvas) after(arrangeSelection(canvas, 'front'));
      },
      'bring-forward': () => {
        const canvas = canvasOf();
        if (canvas) after(arrangeSelection(canvas, 'forward'));
      },
      'send-backward': () => {
        const canvas = canvasOf();
        if (canvas) after(arrangeSelection(canvas, 'backward'));
      },
      'send-back': () => {
        const canvas = canvasOf();
        if (canvas) after(arrangeSelection(canvas, 'back'));
      },
      'flip-h': () => {
        const canvas = canvasOf();
        if (canvas) after(flipSelection(canvas, 'x'));
      },
      'flip-v': () => {
        const canvas = canvasOf();
        if (canvas) after(flipSelection(canvas, 'y'));
      },
      'rotate-cw': () => {
        const canvas = canvasOf();
        if (canvas) after(rotateSelection(canvas, 90));
      },
      'rotate-ccw': () => {
        const canvas = canvasOf();
        if (canvas) after(rotateSelection(canvas, -90));
      },
      'align-left': () => {
        const canvas = canvasOf();
        if (canvas) after(alignSelection(canvas, 'left'));
      },
      'align-center': () => {
        const canvas = canvasOf();
        if (canvas) after(alignSelection(canvas, 'center'));
      },
      'align-right': () => {
        const canvas = canvasOf();
        if (canvas) after(alignSelection(canvas, 'right'));
      },
      'align-top': () => {
        const canvas = canvasOf();
        if (canvas) after(alignSelection(canvas, 'top'));
      },
      'align-middle': () => {
        const canvas = canvasOf();
        if (canvas) after(alignSelection(canvas, 'middle'));
      },
      'align-bottom': () => {
        const canvas = canvasOf();
        if (canvas) after(alignSelection(canvas, 'bottom'));
      },
      'toggle-rulers': () => setRulersVisible((v) => !v),
      'toggle-libraries': () => setLeftDockOpen((v) => !v),
      'toggle-inspector': () => setRightDockOpen((v) => !v),
      'toggle-grid': () => setGridVisible((v) => !v),
      'zoom-in': () => onZoomChange?.(Math.min(400, (zoomRef.current || 100) + 10)),
      'zoom-out': () => onZoomChange?.(Math.max(10, (zoomRef.current || 100) - 10)),
      'zoom-100': () => onZoomChange?.(100),
      shortcuts: () => setShortcutsOpen(true),
      'toggle-motion-mode': () => {
        // The timeline is the Movie Studio workspace, not a switch in the design editor.
      },
      'video-play-pause': () => {
        if (studioRef.current !== 'movie') return;
        const video = useVideoStore.getState();
        if (video.video?.enabled) video.togglePlay();
      },
      'video-stop': () => {
        if (studioRef.current !== 'movie') return;
        useVideoStore.getState().stop();
      },
      'scene-add': () => {
        if (studioRef.current !== 'movie') return;
        if (sceneBudgetAllows()) pageHandlersRef.current.addPage();
      },
      'scene-duplicate': () => {
        if (studioRef.current !== 'movie') return;
        if (sceneBudgetAllows()) pageHandlersRef.current.duplicateActivePage();
      },
      'scene-delete': () => {
        if (studioRef.current !== 'movie') return;
        pageHandlersRef.current.deleteActivePage();
      },
      'animation-panel': () => {
        if (studioRef.current !== 'movie') return;
        window.dispatchEvent(new CustomEvent(OPEN_ANIMATION_PANEL_EVENT));
      },
      'video-export': () => {
        if (studioRef.current !== 'movie') return;
        window.dispatchEvent(new CustomEvent(OPEN_VIDEO_EXPORT_EVENT));
      },
    });

    return () => clearEditorCommands();
  }, [manualSave, startPresent, onZoomChange]);

  // Smart guides while moving objects
  useEffect(() => {
    if (!fabricCanvas || !isCanvasReady) return;
    return installSnapGuides(fabricCanvas);
  }, [fabricCanvas, isCanvasReady]);

  // Ruler-drag guides
  useEffect(() => {
    const onDrop = (ev: Event) => {
      const detail = (
        ev as CustomEvent<{
          orientation: 'v' | 'h';
          clientX: number;
          clientY: number;
        }>
      ).detail;
      const canvas = fabricCanvasRef.current;
      if (!canvas || !detail) return;
      const pt = clientToArtboard(canvas, detail.clientX, detail.clientY);
      const pos = detail.orientation === 'v' ? pt.x : pt.y;
      if (pos < 0 || pos > (detail.orientation === 'v' ? canvas.getWidth() : canvas.getHeight())) {
        return;
      }
      createGuide(canvas, detail.orientation, pos);
    };
    window.addEventListener(DROP_GUIDE_EVENT, onDrop);
    return () => window.removeEventListener(DROP_GUIDE_EVENT, onDrop);
  }, []);

  // Constrain guide dragging
  useEffect(() => {
    if (!fabricCanvas || !isCanvasReady) return;
    const onMoving = (e: fabric.IEvent) => {
      const obj = e.target;
      if (!obj || !isGuideObject(obj)) return;
      constrainGuideMove(obj, fabricCanvas);
    };
    fabricCanvas.on('object:moving', onMoving);
    return () => {
      fabricCanvas.off('object:moving', onMoving);
    };
  }, [fabricCanvas, isCanvasReady]);

  // Artboard grid overlay + snap
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !isCanvasReady) return;
    const el = canvas.getElement()?.parentElement as HTMLElement | null;
    el?.classList.toggle('craftara-grid', gridVisible);
    setGridSnapSize(gridVisible ? 8 : 0);
  }, [gridVisible, fabricCanvas, isCanvasReady]);

  useEffect(() => {
    setSmartSnapEnabled(snapEnabled);
  }, [snapEnabled]);

  useEffect(() => {
    setGuidesLocked(guidesLocked);
    const canvas = fabricCanvasRef.current;
    if (canvas && isCanvasReady) applyGuidesLockState(canvas);
  }, [guidesLocked, fabricCanvas, isCanvasReady]);

  // Ctrl/Meta + mouse wheel zoom
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -10 : 10;
      onZoomChangeRef.current?.(clampZoom(zoomRef.current + delta));
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [isCanvasReady]);

  // Pinch-zoom + two-finger pan (mobile web)
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !isCanvasReady) return;

    return installStageTouchGestures({
      stage,
      getZoom: () => zoomRef.current,
      setZoom: (z) => onZoomChangeRef.current?.(z),
      clampZoom,
      onGestureStart: () => {
        touchGestureActiveRef.current = true;
        const canvas = fabricCanvasRef.current;
        const session = drawSessionRef.current;
        if (session?.shape && canvas) {
          if (session.tool === 'lasso') {
            lassoPointsRef.current = [];
            setLassoActive(false);
            lassoPolyRef.current?.setAttribute('points', '');
          } else if (
            session.tool !== 'crop' ||
            !cropSessionRef.current
          ) {
            canvas.remove(session.shape);
          }
        }
        drawSessionRef.current = null;
        panSessionRef.current = null;
        eraseActiveRef.current = false;
        canvas?.discardActiveObject();
        canvas?.requestRenderAll();
      },
      onGestureEnd: () => {
        touchGestureActiveRef.current = false;
      },
      onDoubleTap: () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const aw = canvas.getWidth() || 1200;
        const ah = canvas.getHeight() || 800;
        const pad = 32;
        const fit = Math.min(
          ((stage.clientWidth - pad) / aw) * 100,
          ((stage.clientHeight - pad) / ah) * 100,
          100
        );
        const current = zoomRef.current;
        // Toggle fit ↔ 100%
        const next =
          Math.abs(current - 100) < 4 ? clampZoom(fit) : clampZoom(100);
        onZoomChangeRef.current?.(next);
      },
    });
  }, [isCanvasReady]);

  // Zoom via CSS transform on Fabric wrapper (keeps internal coords stable)
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !isCanvasReady) return;
    const el = canvas.getElement()?.parentElement as HTMLElement | null;
    if (!el) return;
    el.style.transform = `scale(${zoom / 100})`;
    el.style.transformOrigin = 'top center';
    // Keep Fabric's hit-testing offset in sync after CSS scale changes
    canvas.calcOffset();
  }, [zoom, isCanvasReady]);

  if (loadError) {
    return (
      <div className="canvas-editor error">
        <div className="error-container">
          <h2>Failed to load canvas</h2>
          <p>{loadError}</p>
          <button type="button" onClick={() => location.reload()}>
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`canvas-editor ps-layout-${layoutMode}${compactLayout ? ' ps-compact' : ''}`}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {!isCanvasReady && (
        <div className="canvas-init-overlay">
          <div className="spinner" />
          <p>Initializing canvas…</p>
        </div>
      )}

      {isPhone && (
        <MobileToolDock
          onOpenLibraries={() => setMobileSheet('libraries')}
          onOpenUploads={() => {
            setLeftTab('uploads');
            setMobileSheet('libraries');
          }}
          onOpenInspector={() => {
            setRightTab('layers');
            setMobileSheet('inspector');
          }}
          onOpenMore={() => setMobileSheet('toolMore')}
          onOpenShapes={() => setMobileSheet('shapes')}
          onOpenType={() => setMobileSheet('type')}
        />
      )}

      <OptionsBar
        cropActive={cropActive}
        onCropApply={() => clearCropSession({ apply: true })}
        onCropCancel={() => {
          clearCropSession();
          setTool('select');
        }}
      />

      <div className="ps-body">
        {!isPhone && <ToolStrip />}

        {!isPhone && (
        <aside
          className={`ps-left-dock ${leftDockOpen ? '' : 'collapsed'}`}
          aria-label="Libraries"
          aria-expanded={leftDockOpen}
        >
          <div className="ps-dock-header">
            <span className="ps-dock-title">{leftDockOpen ? 'Libraries' : 'Lib'}</span>
            <button
              type="button"
              className="ps-dock-collapse"
              title={
                leftDockOpen
                  ? 'Hide libraries panel (Ctrl+\\)'
                  : 'Show libraries panel (Ctrl+\\)'
              }
              aria-label={leftDockOpen ? 'Hide libraries panel' : 'Show libraries panel'}
              onClick={() => setLeftDockOpen((v) => !v)}
            >
              {leftDockOpen ? '‹' : '›'}
            </button>
          </div>
          <div className="ps-dock-tabs">
            {(
              [
                ['templates', 'Templates', 'Tpl'],
                ['elements', 'Elements', 'El'],
                ['uploads', 'Uploads', 'Up'],
                ['photo-studio', 'Photo Studio', 'PS'],
              ] as const
            ).map(([id, label, short]) => (
              <button
                key={id}
                type="button"
                className={leftTab === id ? 'active' : ''}
                title={label}
                onClick={() => {
                  setLeftTab(id);
                  if (!leftDockOpen) setLeftDockOpen(true);
                }}
              >
                <span className="ps-dock-tab-full">{label}</span>
                <span className="ps-dock-tab-short">{short}</span>
              </button>
            ))}
          </div>
          <div className="ps-dock-body">
            <DesignPanel
              hideTabs
              activeTab={leftTab}
              onTabChange={setLeftTab}
              projectId={projectId}
              onInsertElement={handleInsertElement}
              onApplyTemplate={(id) => void handleApplyTemplate(id)}
              onUploadImage={handleUploadImage}
              onUploadVideo={handleUploadVideo}
              onInsertUploadUrl={placeImageFromUrl}
              recentUploads={recentUploads}
              uploadBusy={uploadBusy}
              uploadNote={uploadNote}
              onApplyBrandColor={(hex) => {
                useCanvasStore.getState().setSelectedColor(hex);
                const canvas = fabricCanvasRef.current;
                const obj = canvas?.getActiveObject();
                if (canvas && obj) {
                  obj.set('fill', hex);
                  canvas.requestRenderAll();
                  onCanvasChange();
                }
              }}
            />
          </div>
        </aside>
        )}

        <div className={`ps-stage-shell ${rulersVisible ? 'with-rulers' : ''}`}>
          <Rulers
            stageRef={stageRef}
            zoom={zoom}
            artboardWidth={canvasData?.width || currentProject?.width || 1200}
            artboardHeight={canvasData?.height || currentProject?.height || 800}
            visible={rulersVisible}
          />
          <div className="ps-stage" ref={stageRef}>
            <div className="canvas-wrapper">
              <canvas ref={canvasRef} />
              <svg
                className={`ps-lasso-layer ${lassoActive ? 'is-active' : ''}`}
                width={canvasData?.width || currentProject?.width || 1200}
                height={canvasData?.height || currentProject?.height || 800}
                viewBox={`0 0 ${canvasData?.width || currentProject?.width || 1200} ${canvasData?.height || currentProject?.height || 800}`}
                style={{
                  width: canvasData?.width || currentProject?.width || 1200,
                  height: canvasData?.height || currentProject?.height || 800,
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top center',
                }}
                aria-hidden
              >
                <polyline
                  ref={lassoPolyRef}
                  fill="rgba(13, 115, 119, 0.12)"
                  stroke="#0d7377"
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points=""
                />
              </svg>
              <CommentsOverlay
                comments={comments}
                artboardWidth={currentProject?.width || canvasData?.width || 1200}
                artboardHeight={currentProject?.height || canvasData?.height || 800}
                zoom={zoom}
                active={selectedTool === 'comment'}
                onAdd={(x, y, body) => {
                  void addComment({ body, x, y });
                }}
                onResolve={(id) => {
                  const c = comments.find((row) => row.commentId === id);
                  void resolveComment(id, !c?.resolved);
                }}
                onDelete={(id) => {
                  void deleteComment(id);
                }}
              />
            </div>
          </div>
        </div>

        {!isPhone && (
        <aside
          className={`ps-right-dock ${rightDockOpen ? '' : 'collapsed'}`}
          aria-label="Inspector"
          aria-expanded={rightDockOpen}
        >
          <div className="ps-dock-header">
            <button
              type="button"
              className="ps-dock-collapse"
              title={
                rightDockOpen
                  ? 'Hide inspector panel (Ctrl+Shift+\\)'
                  : 'Show inspector panel (Ctrl+Shift+\\)'
              }
              aria-label={
                rightDockOpen ? 'Hide inspector panel' : 'Show inspector panel'
              }
              onClick={() => setRightDockOpen((v) => !v)}
            >
              {rightDockOpen ? '›' : '‹'}
            </button>
            <span className="ps-dock-title">
              {rightDockOpen ? 'Inspector' : 'Ins'}
            </span>
          </div>
          <div className="ps-dock-tabs">
            {(
              [
                ['properties', 'Properties', 'Pr'],
                ['layers', 'Layers', 'Ly'],
                ['history', 'History', 'Hi'],
                ...(motionMode ? ([['motion', 'Motion', 'Mo']] as const) : []),
              ] as const
            ).map(([id, label, short]) => (
              <button
                key={id}
                type="button"
                className={rightTab === id ? 'active' : ''}
                title={label}
                onClick={() => {
                  setRightTab(id);
                  if (!rightDockOpen) setRightDockOpen(true);
                }}
              >
                <span className="ps-dock-tab-full">{label}</span>
                <span className="ps-dock-tab-short">{short}</span>
              </button>
            ))}
          </div>
          <div className="ps-dock-body">
            {rightTab === 'properties' ? (
              <PropertiesPanel
                canvas={fabricCanvas}
                ready={isCanvasReady}
                onChange={onCanvasChange}
                motionTools={motionMode}
              />
            ) : rightTab === 'layers' ? (
              <LayersPanel
                canvas={fabricCanvas}
                ready={isCanvasReady}
                onChange={onCanvasChange}
              />
            ) : rightTab === 'motion' ? (
              <AnimationPanel
                canvas={fabricCanvas}
                ready={isCanvasReady}
                onChange={onCanvasChange}
              />
            ) : (
              <HistoryPanel />
            )}
          </div>
        </aside>
        )}
      </div>

      {pages.length > 0 && !isPhone && motionMode && (
        <VideoTimelinePanel
          pages={pages}
          activePageId={activePageId}
          thumbnails={pageThumbs}
          onSelect={selectPage}
          onRename={renamePage}
          onReorder={reorderPages}
        />
      )}

      {pages.length > 0 && !isPhone && !motionMode && (
        <PagesStrip
          pages={pages}
          activePageId={activePageId}
          thumbnails={pageThumbs}
          onSelect={selectPage}
          onAdd={addPage}
          onDuplicate={duplicateActivePage}
          onDelete={deleteActivePage}
          onRename={renamePage}
          onMove={moveActivePage}
          onReorder={reorderPages}
        />
      )}

      {isPhone && (
        <MobileBottomBar
          zoom={zoom}
          onZoomChange={(z) => onZoomChange?.(z)}
          onZoomFit={() => {
            const stage = stageRef.current;
            const w = currentProject?.width || canvasData?.width || 0;
            const h = currentProject?.height || canvasData?.height || 0;
            if (!stage || !w || !h || !onZoomChange) return;
            const pad = 48;
            const fit = Math.min(
              ((stage.clientWidth - pad) / w) * 100,
              ((stage.clientHeight - pad) / h) * 100,
              100
            );
            onZoomChange(clampZoom(fit));
          }}
          artboardLabel={`${currentProject?.width || canvasData?.width || 0}×${currentProject?.height || canvasData?.height || 0}`}
          pages={pages}
          activePageId={activePageId}
          thumbnails={pageThumbs}
          onSelectPage={selectPage}
          onAddPage={addPage}
          onDuplicatePage={duplicateActivePage}
          onDeletePage={deleteActivePage}
          motionMode={motionMode}
        />
      )}

      <footer className="ps-statusbar">
        <span>
          Document: <strong>{currentProject?.name || 'Untitled'}</strong>
        </span>
        <span>
          Artboard:{' '}
          <strong>
            {currentProject?.width || canvasData?.width || 0} ×{' '}
            {currentProject?.height || canvasData?.height || 0}
          </strong>
        </span>
        <span className={`ps-save-pill ${isSaving ? 'saving' : ''} ${saveError ? 'error' : ''}`}>
          {saveError
            ? 'Save failed'
            : isSaving
              ? 'Saving…'
              : lastSaved
                ? `Saved ${lastSaved.toLocaleTimeString()}`
                : isDirty
                  ? 'Unsaved changes'
                  : 'Ready'}
        </span>

        <button
          type="button"
          className={`ps-status-toggle ${leftDockOpen || mobileSheet === 'libraries' ? 'on' : ''}`}
          title="Toggle libraries panel (Ctrl+\\)"
          aria-pressed={leftDockOpen || mobileSheet === 'libraries'}
          onClick={() => {
            if (isPhone) {
              setMobileSheet((s) => (s === 'libraries' ? null : 'libraries'));
            } else {
              setLeftDockOpen((v) => !v);
            }
          }}
        >
          Libraries
        </button>
        <button
          type="button"
          className={`ps-status-toggle ${rightDockOpen || mobileSheet === 'inspector' ? 'on' : ''}`}
          title="Toggle inspector panel (Ctrl+Shift+\\)"
          aria-pressed={rightDockOpen || mobileSheet === 'inspector'}
          onClick={() => {
            if (isPhone) {
              setMobileSheet((s) => (s === 'inspector' ? null : 'inspector'));
            } else {
              setRightDockOpen((v) => !v);
            }
          }}
        >
          Inspector
        </button>
        <button
          type="button"
          className={`ps-status-toggle ${rulersVisible ? 'on' : ''}`}
          title="Toggle rulers (Ctrl+R) — drag from rulers to place guides"
          aria-pressed={rulersVisible}
          onClick={() => setRulersVisible((v) => !v)}
        >
          Rulers
        </button>
        <button
          type="button"
          className={`ps-status-toggle ${gridVisible ? 'on' : ''}`}
          title="Toggle 8px grid + snap"
          onClick={() => setGridVisible((v) => !v)}
        >
          Grid
        </button>
        <button
          type="button"
          className={`ps-status-toggle ${snapEnabled ? 'on' : ''}`}
          title="Smart snap to edges / centers / guides"
          onClick={() => setSnapEnabled((v) => !v)}
        >
          Snap
        </button>
        <button
          type="button"
          className={`ps-status-toggle ${guidesLocked ? 'on' : ''}`}
          title="Lock ruler guides in place"
          onClick={() => setGuidesLockedState((v) => !v)}
        >
          Lock guides
        </button>
        <button
          type="button"
          className="ps-status-toggle"
          title="Clear all ruler guides"
          onClick={() => {
            const canvas = fabricCanvasRef.current;
            if (canvas) clearGuides(canvas);
          }}
        >
          Clear guides
        </button>
        <button
          type="button"
          className={`ps-status-toggle ${selectedTool === 'comment' ? 'on' : ''}`}
          title="Comment mode — click artboard to pin feedback"
          onClick={() =>
            setTool(selectedTool === 'comment' ? 'select' : 'comment')
          }
        >
          Comments {comments.length ? `(${comments.length})` : ''}
        </button>

        <EditorZoomControls
          variant="status"
          zoom={zoom}
          onZoomChange={(z) => onZoomChange?.(z)}
          onZoomFit={() => {
            const stage = stageRef.current;
            const w = currentProject?.width || canvasData?.width || 0;
            const h = currentProject?.height || canvasData?.height || 0;
            if (!stage || !w || !h || !onZoomChange) return;
            const pad = 48;
            const fit = Math.min(
              ((stage.clientWidth - pad) / w) * 100,
              ((stage.clientHeight - pad) / h) * 100,
              100
            );
            onZoomChange(clampZoom(fit));
          }}
        />
      </footer>

      <SaveIndicator
        isSaving={isSaving}
        lastSaved={lastSaved}
        error={saveError}
        showDuration={2000}
      />

      {isPhone && (
        <>
          <MobileSheet
            open={mobileSheet === 'libraries'}
            title="Libraries"
            onClose={() => setMobileSheet(null)}
          >
            <div className="ps-mobile-sheet-tabs">
              {(
                [
                  ['templates', 'Templates'],
                  ['elements', 'Elements'],
                  ['uploads', 'Uploads'],
                  ['photo-studio', 'Photo Studio'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={leftTab === id ? 'active' : ''}
                  onClick={() => setLeftTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <DesignPanel
              hideTabs
              activeTab={leftTab}
              onTabChange={setLeftTab}
              projectId={projectId}
              onInsertElement={(el) => {
                handleInsertElement(el);
                setMobileSheet(null);
              }}
              onApplyTemplate={(id) => {
                void handleApplyTemplate(id);
                setMobileSheet(null);
              }}
              onUploadImage={async (file) => {
                await handleUploadImage(file);
                setMobileSheet(null);
              }}
              onUploadVideo={async (file) => {
                await handleUploadVideo(file);
                setMobileSheet(null);
              }}
              onInsertUploadUrl={(url) => {
                void placeImageFromUrl(url);
                setMobileSheet(null);
              }}
              recentUploads={recentUploads}
              uploadBusy={uploadBusy}
              uploadNote={uploadNote}
              onApplyBrandColor={(hex) => {
                useCanvasStore.getState().setSelectedColor(hex);
                const canvas = fabricCanvasRef.current;
                const obj = canvas?.getActiveObject();
                if (canvas && obj) {
                  obj.set('fill', hex);
                  canvas.requestRenderAll();
                  onCanvasChange();
                }
              }}
            />
          </MobileSheet>

          <MobileSheet
            open={mobileSheet === 'inspector'}
            title="Inspector"
            onClose={() => setMobileSheet(null)}
          >
            <div className="ps-mobile-sheet-tabs">
              {(
                [
                  ['properties', 'Properties'],
                  ['layers', 'Layers'],
                  ['history', 'History'],
                  ...(motionMode ? ([['motion', 'Motion']] as const) : []),
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={rightTab === id ? 'active' : ''}
                  onClick={() => setRightTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            {rightTab === 'properties' ? (
              <PropertiesPanel
                canvas={fabricCanvas}
                ready={isCanvasReady}
                onChange={onCanvasChange}
                motionTools={motionMode}
              />
            ) : rightTab === 'layers' ? (
              <LayersPanel
                canvas={fabricCanvas}
                ready={isCanvasReady}
                onChange={onCanvasChange}
              />
            ) : rightTab === 'motion' ? (
              <AnimationPanel
                canvas={fabricCanvas}
                ready={isCanvasReady}
                onChange={onCanvasChange}
              />
            ) : (
              <HistoryPanel />
            )}
          </MobileSheet>

          <MobileSheet
            open={mobileSheet === 'toolMore'}
            title="More tools"
            heightRatio={0.45}
            onClose={() => setMobileSheet(null)}
          >
            <MobileMoreToolsGrid onPicked={() => setMobileSheet(null)} />
          </MobileSheet>

          <MobileSheet
            open={mobileSheet === 'shapes'}
            title="Shapes"
            heightRatio={0.5}
            onClose={() => setMobileSheet(null)}
          >
            <MobileShapePicker onPicked={() => setMobileSheet(null)} />
          </MobileSheet>

          <MobileSheet
            open={mobileSheet === 'type'}
            title="Type"
            heightRatio={0.4}
            onClose={() => setMobileSheet(null)}
          >
            <MobileTypePicker onPicked={() => setMobileSheet(null)} />
          </MobileSheet>
        </>
      )}

      <ExportDialog
        open={exportOpen}
        canvas={fabricCanvas}
        projectName={currentProject?.name || 'craftara-export'}
        pages={pages}
        activePageId={activePageId}
        onPrepareExport={() => {
          flushActivePage();
        }}
        onClose={() => setExportOpen(false)}
      />

      <VideoExportDialog
        open={videoExportOpen}
        projectId={currentProject?.projectId ?? null}
        projectName={currentProject?.name || 'craftara-video'}
        getPages={() => {
          stopPreview();
          return flushActivePage();
        }}
        onClose={() => setVideoExportOpen(false)}
      />

      <PresentMode
        open={presentOpen}
        slides={presentSlides}
        title={currentProject?.name || 'Presentation'}
        onClose={() => setPresentOpen(false)}
      />

      <ShortcutsHelp open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {saveError && (
        <div className="error-notification">
          <div className="error-content">
            <p>{saveError}</p>
            <button type="button" onClick={() => void requestEditorSave()}>
              Retry
            </button>
            <button type="button" onClick={clearError}>
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
